import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Circle, X } from 'lucide-react';
import { doc, type DocumentReference } from 'firebase/firestore';
import { useAuth } from '../contexts/useAuth';
import { useDepartments } from '../contexts/useDepartments';
import { MultiSelectDropdown } from './ui/MultiSelectDropdown';
import {
  getCrossAuditDepartmentOptions,
  HEAD_WARD_NURSE_CROSS_AUDIT_AUDITOR_DEPARTMENT_IDS,
  HEAD_WARD_NURSE_CROSS_AUDIT_HIDE_OPD_PRIMARY_PC_FOR_HOME_DEPARTMENT_IDS,
  HEAD_WARD_NURSE_ECC_ONLY_CROSS_AUDIT_HOME_DEPARTMENT_IDS,
} from '../lib/crossAudit';
import {
  clearDepartmentDraft,
  makeSubmissionDraftKey,
  markSubmissionAsLegacy,
  persistDepartmentDraft,
  readDraftFromLocalStorage,
  submissionDraftMatchesScope,
  writeDraftToLocalStorage,
  type SubmissionDocument,
  type SubmissionMetadata,
} from '../services/submissions';
import { computeChartCountFromData, normalizeSubmissionShape } from '../services/submissionShape';
import { DEFAULT_COMPLIANCE_CATEGORIES, getCategoryById, type ComplianceCategory } from '../lib/complianceCategories';
import { getChecklist, type ChecklistSection } from '../lib/checklists';
import {
  completenessFromUnknownRow,
  overallConsistencyUnweightedAverage,
  perChartConsistencyPctFromStrings,
  resolveNursingFocusTreatmentsForOR,
} from '../lib/nursingComplianceScoring';
import { db } from '../lib/firestore';
import {
  formatWardSubmitClosedMessage,
  isWardSubmitRetired,
  resolveWardSubmitDisplayTitle,
  wardSubmitSubmitClosedMessage,
} from '../lib/wardSubmitRetired';
import {
  getFirestoreErrorCode,
  syncParentNursingSubmittedFields,
  type ParentNursingSyncPayload,
  type ParentNursingSyncTelemetryEvent,
} from '../lib/parentNursingSync';
import { getFirestorePathDepartmentId, normalizeDepartmentId } from '../utils/normalizeIds';
import { NurseNotesXlsxImportPanel } from './compliance/NurseNotesXlsxImportPanel';
import { BulkChoiceFillButtons } from './compliance/BulkChoiceFillButtons';
import { fillAnswersRow, type BulkChoice } from './compliance/bulkChoiceFill';
import {
  CrossAuditSliceFirestoreSync,
  crossAuditSliceToRowPatch,
  enrichCrossAuditDocFromMetrics,
} from './audit/CrossAuditSidebar';
import { CARD_CONFIGS } from '../lib/compliance_constants';
import {
  departmentRequiresNursingCrossAudit,
} from '../lib/nursingDepartmentPolicy';
import { type CrossAuditMergeDiagnostics } from '../hooks/useSubmissionHydration';
import {
  formatReportingMonthLabelBuddhist,
  canonicalReportingMonth,
  getReportingMonth,
  isEditableMonth,
} from '../lib/reportingMonth';
import {
  hasDepartmentCategoriesConfigured,
  normalizeDepartmentCategories,
  type Department,
} from '../lib/departments';
import { NURSING_NOTE_IDENTIFIERS } from '../hooks/useComplianceCategories';
import { NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS } from '../lib/medErrorWardMap';
import { logAuditEvent } from '../lib/audit';
import { resolveCanonicalCategoryId } from '../lib/canonicalIds';
import { medErrorRemarksValidForWeek } from '../lib/medErrorRemarksValidation';
import {
  allowsExtraComplianceCharts,
  extraComplianceChartAddButtonLabel,
  getDefaultNursingChartTitles,
  getNextNursingChartTitle,
  getNursingChartTitleFallbackPool,
  getNursingChartTypeFromTitle,
  normalizeNursingChartTitles,
  requiredComplianceChartSlots,
  resolveMedErrorVisibleChartCount,
  type NursingChartType,
} from './ComplianceHub.helpers';

const MED_ERROR_TITLE = 'นิเทศระบบยา';
const LAUNCH_CATEGORY_TITLE = 'บันทึกทางการพยาบาล';
const NURSE_NOTES_XLSX_IMPORT_UI_ENABLED =
  import.meta.env.VITE_NURSE_NOTES_IMPORT_ENABLED === 'true';
const shouldDebugHydration = () =>
  import.meta.env.DEV || (typeof window !== 'undefined' && localStorage.getItem('debugHydration') === '1');

/** Safe for template literals, JSX, and console — avoids ToPrimitive throws on exotic .message values. */
const toSafeErrorDisplayString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return 'Unknown error';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value instanceof Error) return toSafeErrorDisplayString(value.message);
  try {
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    return String(value);
  } catch {
    return 'Unknown error';
  }
};

const compactErrorSummary = (error: unknown) => {
  const errorCode = typeof error === 'object' && error && 'code' in error ? (error as { code?: unknown }).code : undefined;
  const rawMessage =
    typeof error === 'object' && error && 'message' in error ? (error as { message?: unknown }).message : undefined;
  const errorMessage =
    rawMessage !== undefined ? toSafeErrorDisplayString(rawMessage) : toSafeErrorDisplayString(error);
  const errorName =
    typeof error === 'object' && error && 'name' in error ? (error as { name?: unknown }).name : 'unknown';
  const stackPreview =
    typeof error === 'object' && error && 'stack' in error && typeof (error as { stack?: unknown }).stack === 'string'
      ? ((error as { stack: string }).stack.split('\n').slice(0, 2).join(' | ') || null)
      : null;

  return {
    errorCode: typeof errorCode === 'string' ? errorCode : undefined,
    errorMessage,
    errorName: typeof errorName === 'string' ? errorName : 'unknown',
    stackPreview,
  };
};

const getArrayLength = (value: unknown) => (Array.isArray(value) ? value.length : 0);

const compactSubmitCounters = (snapshot: SubmissionDocument, chartIdx: number) => {
  const answersRow = Array.isArray(snapshot.answers) ? snapshot.answers[chartIdx] : [];
  const historyRow = Array.isArray(snapshot.history) ? snapshot.history[chartIdx] : [];

  return {
    chartIndex: chartIdx,
    answersRows: getArrayLength(snapshot.answers),
    answersInCurrentChart: getArrayLength(answersRow),
    diseaseCount: getArrayLength(snapshot.disease),
    focusCount: getArrayLength(snapshot.focus),
    treatmentsCount: getArrayLength(snapshot.treatments),
    problemsCount: getArrayLength(snapshot.problems),
    suggestionsCount: getArrayLength(snapshot.suggestions),
    datesCount: getArrayLength(snapshot.dates),
    shiftsCount: getArrayLength(snapshot.shifts),
    hnCount: getArrayLength(snapshot.hn),
    historyRows: getArrayLength(snapshot.history),
    historyItemsInCurrentChart: getArrayLength(historyRow),
    supervisionRecordsCount: getArrayLength(snapshot.supervisionRecords),
    chartTitlesCount: getArrayLength(snapshot.chartTitles),
    submittedCount: getArrayLength(snapshot.submitted),
    incompleteCount: getArrayLength(snapshot.incomplete),
    versionsCount: getArrayLength(snapshot.versions),
  };
};

const CARD_WARNING_TITLES = new Set([LAUNCH_CATEGORY_TITLE, MED_ERROR_TITLE]);

export const resolveSubmissionDepartmentRef = ({
  isSupervisorNurseRole,
  selectedDepartmentRef,
  submissionDepartmentId,
}: {
  isSupervisorNurseRole: boolean;
  selectedDepartmentRef: DocumentReference | null;
  submissionDepartmentId: string | null;
}) => {
  if (isSupervisorNurseRole) return null;
  if (selectedDepartmentRef) return selectedDepartmentRef;
  if (submissionDepartmentId) return doc(db, "departments", submissionDepartmentId);
  return null;
};

const formatPeriodicityLabel = (periodicity?: string) => {
  switch (periodicity?.toLowerCase()) {
    case 'weekly':
      return 'รายสัปดาห์';
    case 'quarterly':
      return 'รายไตรมาส';
    case 'yearly':
      return 'รายปี';
    case 'monthly':
      return 'รายเดือน';
    default:
      return periodicity || 'Monthly compliance';
  }
};

const SUPERVISION_RECIPIENT_OPTIONS = [
  { value: "individual", label: "รายบุคคล" },
  { value: "custom", label: "ระบุ" },
  { value: "group", label: "รายกลุ่ม" },
];

const SUPERVISION_ACTIVITY_OPTIONS = [
  { value: "rounds", label: "การเยี่ยมตรวจทางการพยาบาล (nursing rounds)" },
  { value: "conference", label: "การประชุมปรึกษาทางการพยาบาล (nursing conference)" },
  { value: "teaching", label: "การสอน (teaching)" },
  { value: "problem-solving", label: "การแก้ปัญหา (problem-solving)" },
  { value: "counseling", label: "การให้คำปรึกษา" },
  { value: "observation", label: "การสังเกต (observation)" },
  { value: "collaboration", label: "การร่วมมือปฏิบัติงาน" },
];

const SUPERVISION_ISSUE_OPTIONS = [
  { value: "manpower", label: "การจัดอัตรากำลัง" },
  { value: "assignment", label: "การมอบหมายงาน" },
  { value: "equipment", label: "อุปกรณ์เครื่องมือเครื่องใช้" },
  { value: "environment", label: "สถานที่สิ่งแวดล้อมและ 5 ส" },
  { value: "energy", label: "อนุรักษ์พลังงาน" },
  { value: "disease-standard", label: "มาตรฐานการดูแลผู้ป่วยรายโรค" },
  { value: "medication", label: "การบริหารยา" },
  { value: "blood", label: "การบริหารเลือด" },
  { value: "identification", label: "การบ่งชี้" },
  { value: "specimen", label: "การเก็บสิ่งส่งตรวจ" },
  { value: "fall", label: "การป้องกันFall" },
  { value: "needleandsharp", label: "การป้องกันเข็มทิ่มตำ" },
  { value: "phlebitis", label: "การป้องกัน Phlebitis" },
  { value: "pre-op", label: "การเตรียมผู้ป่วยส่งทำผ่าตัด /หัตถการ" },
  { value: "isitdown", label: "ISITDOWN" },
  { value: "nurse_notes", label: "บันทึกทางการพยาบาล" },
  { value: "ic", label: "IC" },
  { value: "service", label: "พฤติกรรมบริการ" },
  { value: "attire", label: "การแต่งกาย" },
  { value: "self-development", label: "การอบรมพัฒนาตนเอง" },
  { value: "staff-health", label: "การส่งเสริมสขภาพกายใจเจ้าหน้าที่" },
  { value: "patient-education", label: "การสอนแนะนำผู้ป่วย" },
  { value: "other", label: "อื่นๆระบุ......" },
];

type SupervisionRecord = {
  recipients: string[];
  recipientsDetail: string;
  activities: string[];
  issues: string[];
  issueOther: string;
  problemDetail: string;
  solution: string;
  followUp: string;
};

type SupervisionErrorState = {
  recipients: boolean;
  recipientsDetail: boolean;
  activities: boolean;
  issues: boolean;
  issueOther: boolean;
  problemDetail: boolean;
  solution: boolean;
  followUp: boolean;
  date: boolean;
};

const createSupervisionRecord = (): SupervisionRecord => ({
  recipients: [],
  recipientsDetail: "",
  activities: [],
  issues: [],
  issueOther: "",
  problemDetail: "",
  solution: "",
  followUp: "",
});

const createSupervisionErrorState = (): SupervisionErrorState => ({
  recipients: false,
  recipientsDetail: false,
  activities: false,
  issues: false,
  issueOther: false,
  problemDetail: false,
  solution: false,
  followUp: false,
  date: false,
});

const sanitizeStringArray = (values?: Array<string | undefined>) =>
  Array.isArray(values) ? values.map((val) => val ?? "") : [];

const isDocumentReference = (value: unknown): value is DocumentReference => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.id === "string" && v.id.trim().length > 0;
};

const sanitizeSupervisionRecord = (record?: SupervisionRecord | null): SupervisionRecord => {
  const safeRecord = record ?? createSupervisionRecord();
  return {
    recipients: sanitizeStringArray(safeRecord.recipients),
    recipientsDetail: safeRecord.recipientsDetail ?? "",
    activities: sanitizeStringArray(safeRecord.activities),
    issues: sanitizeStringArray(safeRecord.issues),
    issueOther: safeRecord.issueOther ?? "",
    problemDetail: safeRecord.problemDetail ?? "",
    solution: safeRecord.solution ?? "",
    followUp: safeRecord.followUp ?? "",
  };
};

const DEFAULT_CHARTS = 10;
const DEFAULT_CHOICE = "NA";
const CHOICE_VALUES = new Set(["1", "0", DEFAULT_CHOICE]);

const sanitizeChoice = (val?: string) => (val && CHOICE_VALUES.has(val) ? val : DEFAULT_CHOICE);
const hasNonDefaultChoice = (val?: string) => sanitizeChoice(val) !== DEFAULT_CHOICE;

/** Returns true if every criterion has a valid choice (1, 0, or NA). */
const hasAllCriteriaFilled = (answersRow: string[] | undefined): boolean => {
  if (!answersRow || answersRow.length === 0) return false;
  return answersRow.every((v) => {
    const s = typeof v === "string" ? v.trim() : String(v ?? "");
    return s.length > 0 && CHOICE_VALUES.has(s);
  });
};

/**
 * Sidebar / card progress: a chart must not count as "draft-complete" when every cell is still the default NA
 * (initial state looks "filled" but the user has not chosen 1 or 0 anywhere). Submitted charts still count via `submitted[]`.
 */
export function hasNursingDraftRowCompleteForProgress(answersRow: string[] | undefined): boolean {
  if (!hasAllCriteriaFilled(answersRow)) return false;
  return (answersRow as string[]).some((v) => hasNonDefaultChoice(v));
}

/**
 * Launch-card % for nursing: count submitted or draft-complete charts in **any** tab order
 * (canonical requirement = 8). Prefix-only counting breaks when ตรวจไขว้ / added charts
 * sit at the end while earlier placeholders stay empty — same data showed ~71% with 8 real submits.
 * Denominator stays `requiredChartSlots` (8); numerator is min(doneAcrossAllTabs, required).
 */
export function computeNursingCardProgress(
  chartCount: number,
  submitted: boolean[],
  filled: boolean[],
  requiredChartSlots?: number,
): number {
  if (!Number.isFinite(chartCount) || chartCount <= 0) return 0;
  const reqCandidate = requiredChartSlots ?? chartCount;
  const denom =
    Number.isFinite(reqCandidate) && reqCandidate > 0 ? Math.floor(reqCandidate) : chartCount;
  let done = 0;
  for (let i = 0; i < chartCount; i += 1) {
    if (submitted[i] || filled[i]) done += 1;
  }
  return Math.round((Math.min(done, denom) / denom) * 100);
}

type ChecklistDisplaySection = ChecklistSection & { rows: { label: string; index: number }[] };

const getRequiredNursingCharts = () => getCategoryById("nurse_notes")?.charts ?? 8;

function getCategoryConfig(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  if (!title) return undefined;
  return categories.find((category) => category.title === title || category.id === title);
}

/** Resolve display title to canonical category doc ID. When adding new category aliases,
 * update COMPLIANCE_CATEGORY_ALIASES in canonicalIds.ts and ensure category config aligns. */
function resolveCategoryDocId(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  if (!title) return "";
  const normalized = title.trim();
  if (!normalized) return "";
  const match = categories.find((category) => category.id === normalized || category.title === normalized);
  return match?.id ?? normalized;
}

function getChartCount(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  return getCategoryConfig(title, categories)?.charts ?? DEFAULT_CHARTS;
}

function getPeriodLabel(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  return getCategoryConfig(title, categories)?.periodLabel || "ครั้งที่";
}

function getTeamLabel(title?: string, categories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES) {
  return getCategoryConfig(title, categories)?.teamLabel || "";
}

function computeProgressPercent(completed: number, total: number) {
  if (!Number.isFinite(completed) || !Number.isFinite(total) || total <= 0) return 0;
  const safeCompleted = Math.max(0, Math.min(completed, total));
  return Math.round((safeCompleted / total) * 100);
}

type ChecklistCard = {
  id: number;
  title: string;
  categoryDocId: string;
  subtitle: string;
  progress: number;
  color: string;
  warning?: boolean;
  alertText?: string;
  iconColor?: string;
};

// Lazy initialization to avoid TDZ issues - convert to function
const getBaseCards = (): ChecklistCard[] => {
  return DEFAULT_COMPLIANCE_CATEGORIES.map((category, index) => ({
    id: index + 1,
    title: category.title,
    categoryDocId: category.id,
    subtitle: "Monthly compliance",
    progress: 0,
    color: category.color ?? getPalette().primary,
  }));
};

// Remove duplicate export - BASE_COMPLIANCE_CARD_TITLES is already exported from ComplianceHub.logic.ts
// Use getBaseCards() function instead of baseCards constant

export const computeAllowedCategories = (
  scopedDepartments: Department[],
  hasWildcardDepartmentScope: boolean,
  isAdmin: boolean,
  complianceCategories: ComplianceCategory[] = DEFAULT_COMPLIANCE_CATEGORIES,
  currentUserId?: string | null,
  scopedTeamIds: string[] = [],
  _hasNNTeamRole: boolean = false,
  teamAllowedCategoryIds: string[] = [],
) => {
  const scopedDepartmentIds = new Set(scopedDepartments.map((dept) => dept.id));
  const allowedTeamIds = scopedTeamIds.filter(Boolean);
  const hasWildcardTeamScope = allowedTeamIds.includes('*');
  const activeCategories = complianceCategories.filter((category) => category.active !== false);

  const categories = scopedDepartments.flatMap((dept) => normalizeDepartmentCategories(dept, activeCategories));
  const normalizedCategories = Array.from(new Set(categories));

  const hasConfiguredCategories = scopedDepartments.some(hasDepartmentCategoriesConfigured);

  const shouldFallbackToAllCategories = !hasConfiguredCategories && (hasWildcardDepartmentScope || isAdmin);

  const baseCategories: ComplianceCategory[] = (() => {
    if (!scopedDepartments.length) {
      return shouldFallbackToAllCategories
        ? activeCategories
        : activeCategories.filter((category) => !category.departmentIds?.length);
    }

    if (shouldFallbackToAllCategories) {
      return activeCategories;
    }

    return normalizedCategories.map(
      (ref) =>
        activeCategories.find((category) => category.id === ref || category.title === ref) ?? {
          id: ref,
          title: ref,
        },
    );
  })();

  const allowedTitles = baseCategories
    .filter((category) => {
      const matchesDepartmentScope =
        !category.departmentIds?.length || category.departmentIds.some((id) => scopedDepartmentIds.has(id));
      const matchesUserScope = !category.userIds?.length || (currentUserId ? category.userIds.includes(currentUserId) : false);
      const matchesTeamScope = hasWildcardTeamScope || !category.teamId || allowedTeamIds.includes(category.teamId);
      return category.active !== false && matchesDepartmentScope && matchesUserScope && matchesTeamScope;
    })
    .map((category) => category.title)
    .filter((title): title is string => Boolean(title?.trim()));

  const allowedSet = new Set(allowedTitles);

  // Non-admins always need nursing notes + นิเทศระบบยา on the compliance hub
  // (team NN / med_error roles are separate from normal-page scope).
  if (!isAdmin) {
    complianceCategories
      .filter((category) =>
        NURSING_NOTE_IDENTIFIERS.has(category.id) ||
        NURSING_NOTE_IDENTIFIERS.has(category.title ?? "") ||
        category.id === "med_error" ||
        category.title === MED_ERROR_TITLE,
      )
      .forEach((category) => {
        if (category.title?.trim()) {
          allowedSet.add(category.title);
        }
      });
  }

  // Add team-allowed categories (e.g. ethic) for users with teamAllowedCategoryIds
  const teamCategorySet = new Set(teamAllowedCategoryIds.filter(Boolean));
  if (teamCategorySet.size > 0) {
    complianceCategories
      .filter(
        (category) =>
          teamCategorySet.has(category.id ?? "") || teamCategorySet.has(category.title ?? ""),
      )
      .forEach((category) => {
        if (category.title?.trim()) {
          allowedSet.add(category.title);
        }
      });
  }

  return Array.from(allowedSet.values());
};

function makeArray<T>(len: number, factory: (i: number) => T) {
  return Array.from({ length: len }, (_, i) => factory(i));
}

const NURSING_CHART_TYPES = ["รายโรค", "โรคทั่วไป", "ตรวจไขว้"] as const;

const normalizeSupervisionRecord = (input: any): SupervisionRecord => {
  if (!input || typeof input !== "object") return createSupervisionRecord();
  const record = input as Partial<SupervisionRecord>;
  return {
    recipients: Array.isArray(record.recipients)
      ? (record.recipients.filter((v) => typeof v === "string") as string[])
      : [],
    recipientsDetail: typeof record.recipientsDetail === "string" ? record.recipientsDetail : "",
    activities: Array.isArray(record.activities)
      ? (record.activities.filter((v) => typeof v === "string") as string[])
      : [],
    issues: Array.isArray(record.issues) ? (record.issues.filter((v) => typeof v === "string") as string[]) : [],
    issueOther: typeof record.issueOther === "string" ? record.issueOther : "",
    problemDetail: typeof record.problemDetail === "string" ? record.problemDetail : "",
    solution: typeof record.solution === "string" ? record.solution : "",
    followUp: typeof record.followUp === "string" ? record.followUp : "",
  };
};

const normalizeSupervisionRecords = (source: any, count: number) =>
  makeArray(count, (i) => normalizeSupervisionRecord(Array.isArray(source) ? source[i] : undefined));

const getCurrentMonthKey = (title: string, date = new Date(), departmentId: string | null = null, userId: string | null = null) =>
  makeSubmissionDraftKey(departmentId, title, getReportingMonth(date), userId);

function normalizeArray<T>(source: T[] | undefined, count: number, filler: T) {
  const safe = Array.isArray(source) ? source : [];
  return makeArray(count, (i) => (safe[i] !== undefined ? (safe[i] as T) : filler));
}

function normalizeStringMatrix(source: any, rows: number, cols: number) {
  return makeArray(rows, (i) => {
    const row = Array.isArray(source?.[i]) ? (source[i] as any[]) : [];
    return makeArray(cols, (j) => {
      const val = row[j];
      if (typeof val === "string") return val;
      if (typeof val === "number") return String(val);
      return "";
    });
  });
}

function readDraftWithFallback(monthKey: string, legacyMonthKey: string, expectedDeptId: string | null = null) {
  if (typeof window === "undefined") return { data: null };
  const rawPrimary = readDraftFromLocalStorage(monthKey);
  if (rawPrimary) {
    if (submissionDraftMatchesScope(rawPrimary, expectedDeptId)) {
      return { data: rawPrimary };
    }
    if (expectedDeptId?.trim()) {
      try {
        localStorage.removeItem(monthKey);
      } catch {
        /* ignore */
      }
    }
  }
  if (!legacyMonthKey || legacyMonthKey === monthKey) return { data: null };
  const rawLegacy = readDraftFromLocalStorage(legacyMonthKey);
  if (!rawLegacy) return { data: null };
  if (!submissionDraftMatchesScope(rawLegacy, expectedDeptId)) {
    if (expectedDeptId?.trim()) {
      try {
        localStorage.removeItem(legacyMonthKey);
      } catch {
        /* ignore */
      }
    }
    return { data: null };
  }
  writeDraftToLocalStorage(monthKey, rawLegacy, Boolean((rawLegacy as { draft?: boolean }).draft));
  return { data: rawLegacy, migrated: true };
}

function getStoredChartCount(monthKey: string, legacyMonthKey: string, fallback: number, expectedDeptId: string | null = null) {
  if (typeof window === "undefined") return fallback;
  const { data } = readDraftWithFallback(monthKey, legacyMonthKey, expectedDeptId);
  if (!data) return fallback;
  return computeChartCountFromData(data, fallback);
}

function ProgressBar({ value, color }: { value: number; color: string }) {
  const safeValue = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;

  return (
    <div className="w-full min-w-0 h-2 rounded-full bg-slate-200 overflow-hidden" role="progressbar" aria-valuenow={safeValue} aria-valuemin={0} aria-valuemax={100} aria-label="Progress">
      <motion.div
        className="h-full rounded-full min-w-0 shrink-0"
        style={{ background: color }}
        initial={false}
        animate={{ width: `${safeValue}%` }}
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
      />
    </div>
  );
}

const getPalette = () => ({
  primary: '#6F4CF6',
  green: '#22C55E',
  amber: '#F59E0B',
  red: '#EF4444',
  blue: '#3B82F6',
} as const);

function ChoiceGroup({
  selected,
  onSelect,
  disabled,
  error,
}: {
  selected: string;
  onSelect: (v: string) => void;
  disabled?: boolean;
  error?: boolean;
}) {
  const options = [
    { v: "1", label: "1", color: getPalette().green },
    { v: "0", label: "0", color: getPalette().red },
    { v: "NA", label: "NA", color: "#94A3B8" },
  ];
  return (
    <div
      className={`inline-flex rounded-full overflow-hidden border ${disabled ? "opacity-60" : ""} ${error ? "border-red-500 ring-1 ring-red-400" : "border-slate-200"}`}
      role="group"
      aria-label="ตัวเลือก 1/0/NA"
    >
      {options.map((o, i) => {
        const isActive = selected === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => !disabled && onSelect(o.v)}
            aria-pressed={isActive}
            disabled={disabled}
            className={`px-5 h-10 text-sm font-medium focus:outline-none ${i > 0 ? "border-l" : ""} ${isActive ? "text-white" : "text-slate-700"}`}
            style={{ background: isActive ? o.color : "transparent", borderColor: "#E5E7EB", minWidth: 84 }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}


function SidePanel({
  card,
  onClose,
  onSubmitChart,
  onReportingTableRefresh,
  readOnly,
  allowedCategories,
  hydrationCategories,
  complianceCategories,
  reportingMonthDate,
  departmentId,
  scopedDepartments,
  makeMonthKey,
  chartCountFor,
  periodLabelFor,
  hydrationToken,
  hydrationLoading,
  crossAuditMergeDiagnostics,
  currentUserId,
  submissionMetadata,
  selectedDepartmentRef,
  selectedLofRef,
  requireLofRef,
  suppressDepartmentValidation,
  onNursingFilledCompletenessChange,
  onExitComplete,
  isHeadWardNurse,
}: {
  card: ChecklistCard | null;
  onClose: () => void;
  onSubmitChart?: (chartIndex: number, incomplete?: boolean, revert?: boolean) => void;
  onReportingTableRefresh?: () => void;
  onNursingFilledCompletenessChange?: (filled: boolean[]) => void;
  onExitComplete?: () => void;
  readOnly?: boolean;
  allowedCategories: string[];
  hydrationCategories?: string[];
  complianceCategories?: ComplianceCategory[];
  reportingMonthDate: Date;
  departmentId: string | null;
  scopedDepartments: Department[];
  makeMonthKey: (category: string) => string;
  chartCountFor: (title?: string) => number;
  periodLabelFor: (title?: string) => string;
  hydrationToken: string;
  hydrationLoading?: boolean;
  /** Set when cross-audit docs were loaded but some could not be mapped to a chart (slot/title mismatch). */
  crossAuditMergeDiagnostics?: CrossAuditMergeDiagnostics | null;
  currentUserId: string | null;
  submissionMetadata: SubmissionMetadata;
  selectedDepartmentRef?: DocumentReference | null;
  selectedLofRef?: DocumentReference | null;
  requireLofRef?: boolean;
  suppressDepartmentValidation?: boolean;
  /** With home ward: BLO_BNK HWN → ECC-only picker; ECC HWN → hide OPD_Primary/OPD_PC from cross-audit list. */
  isHeadWardNurse?: boolean;
}) {
  const { auth, firestoreUser } = useAuth();
  const { departments, loading: departmentsLoading } = useDepartments();
  const safeComplianceCategories = React.useMemo(
    () => (complianceCategories?.length ? complianceCategories : DEFAULT_COMPLIANCE_CATEGORIES),
    [complianceCategories],
  );

  const actorSubmissionMetadata = React.useMemo<SubmissionMetadata>(
    () => ({
      ...submissionMetadata,
      submittedBy: auth?.currentUser?.uid ?? submissionMetadata.submittedBy ?? null,
      submittedByEmail: auth?.currentUser?.email ?? submissionMetadata.submittedByEmail ?? null,
      submittedByName:
        auth?.currentUser?.displayName ??
        (typeof (firestoreUser as any)?.displayName === 'string' ? (firestoreUser as any).displayName : null) ??
        submissionMetadata.submittedByName ??
        null,
    }),
    [
      auth?.currentUser?.displayName,
      auth?.currentUser?.email,
      auth?.currentUser?.uid,
      firestoreUser,
      submissionMetadata,
    ],
  );

  const checklistSections = React.useMemo<ChecklistDisplaySection[]>(() => {
    if (!card) return [];
    let idx = 0;
    return getChecklist(card.title).map((section) => ({
      ...section,
      rows: section.items.map((label) => ({ label, index: idx++ })),
    }));
  }, [card]);
  const flatList = React.useMemo(
    () => checklistSections.flatMap((section) => section.rows.map((row) => row.label)),
    [checklistSections],
  );
  const questionCount = flatList.length;
  const isSupervision = !!card && card.title === "นิเทศทางการพยาบาล";
  const hasDetailedForm = !!card && (questionCount > 0 || isSupervision);
  const requiresClinicalInputs = !!card && card.title === "บันทึกทางการพยาบาล";
  const effectiveDepartmentIdForValidation = departmentId ?? selectedDepartmentRef?.id ?? null;
  const isORDepartment = !!effectiveDepartmentIdForValidation && normalizeDepartmentId(effectiveDepartmentIdForValidation) === "OR";
  const allowFreeText = !!card && (card.title === "บันทึกทางการพยาบาล" || card.title === MED_ERROR_TITLE);
  const requiresShiftSelection = !!card && card.title === "เข็มตำ/ของมีคมบาด";
  const requiresHN = !!card && card.title === "SIREN bundle";
  const isNursing = !!card && card.title === "บันทึกทางการพยาบาล";
  const isMedError = !!card && card.title === MED_ERROR_TITLE;
  const nursingChartTitleFallback = React.useMemo(
    () => getNursingChartTitleFallbackPool(departmentId),
    [departmentId],
  );
  const crossAuditPartialLoadHint =
    isNursing &&
    !hydrationLoading &&
    crossAuditMergeDiagnostics &&
    (crossAuditMergeDiagnostics.skippedNoChartIndex > 0 || crossAuditMergeDiagnostics.skippedNoSlot > 0);
  const baseChartCount = chartCountFor(card?.title);
  const [chartCount, setChartCount] = React.useState(baseChartCount);
  const periodLabel = periodLabelFor(card?.title);
  const summaryLabel = periodLabel === "ครั้งที่" ? "ครั้ง" : periodLabel;
  const reportingMonthKey = React.useMemo(() => getReportingMonth(reportingMonthDate), [reportingMonthDate]);
  const reportingMonthLabel = React.useMemo(
    () => formatReportingMonthLabelBuddhist(reportingMonthKey),
    [reportingMonthKey],
  );
  const hydrationCategoriesList = hydrationCategories ?? allowedCategories;
  const isCategoryAllowed = React.useCallback(
    () =>
      !card ||
      allowedCategories.includes(card.title) ||
      hydrationCategoriesList.includes(card.title),
    [allowedCategories, card, hydrationCategoriesList],
  );

  const monthKey = React.useMemo(() => {
    if (!card) return "";
    return makeMonthKey(card.categoryDocId || card.title);
  }, [card, makeMonthKey]);
  const legacyMonthKey = React.useMemo(() => {
    if (!card) return "";
    return getCurrentMonthKey(card.title, reportingMonthDate, departmentId, currentUserId);
  }, [card, reportingMonthDate, departmentId, currentUserId]);

  const [chartIndex, setChartIndex] = React.useState(0);
  const createResetState = React.useCallback(
    (count: number) => ({
      answers: makeArray(count, () => makeArray(questionCount || 0, () => DEFAULT_CHOICE)),
      disease: makeArray(count, () => ""),
      focus: makeArray(count, () => ""),
      treatments: makeArray(count, () => ""),
      problems: makeArray(count, () => makeArray(questionCount || 0, () => "")),
      suggestions: makeArray(count, () => ""),
      shifts: makeArray(count, () => ""),
      dates: makeArray(count, () => ""),
      hn: makeArray(count, () => ""),
      crossAuditByDeptIds: makeArray(count, () => ""),
      submitted: makeArray(count, () => false),
      versions: makeArray(count, () => 0),
      history: makeArray(count, () => [] as any[]),
      incomplete: makeArray(count, () => false),
      supervisionRecords: makeArray(count, () => createSupervisionRecord()),
      chartTitles: isNursing
        ? getDefaultNursingChartTitles(count, departmentId)
        : makeArray(count, (i) => `${periodLabel} ${i + 1}`),
    }),
    [departmentId, isNursing, periodLabel, questionCount],
  );

  const [answers, setAnswers] = React.useState<string[][]>(() => createResetState(chartCount).answers);
  const [disease, setDisease] = React.useState<string[]>(() => createResetState(chartCount).disease);
  const [focus, setFocus] = React.useState<string[]>(() => createResetState(chartCount).focus);
  const [treatments, setTreatments] = React.useState<string[]>(() => createResetState(chartCount).treatments);
  const [problems, setProblems] = React.useState<string[][]>(() => createResetState(chartCount).problems);
  const [suggestions, setSuggestions] = React.useState<string[]>(() => createResetState(chartCount).suggestions);
  const [shifts, setShifts] = React.useState<string[]>(() => createResetState(chartCount).shifts);
  const [dates, setDates] = React.useState<string[]>(() => createResetState(chartCount).dates);
  const [hn, setHn] = React.useState<string[]>(() => createResetState(chartCount).hn);
  const [crossAuditByDeptIds, setCrossAuditByDeptIds] = React.useState<string[]>(
    () => createResetState(chartCount).crossAuditByDeptIds,
  );
  const [supervisionRecords, setSupervisionRecords] = React.useState<SupervisionRecord[]>(() => createResetState(chartCount).supervisionRecords);
  const [chartTitleList, setChartTitleList] = React.useState<string[]>(() => createResetState(chartCount).chartTitles);
  const currentChartTitle = chartTitleList[chartIndex] ?? "";
  const nursingCrossAuditRequired = departmentRequiresNursingCrossAudit(departmentId);
  const isCrossAuditChart =
    isNursing && nursingCrossAuditRequired && getNursingChartTypeFromTitle(currentChartTitle) === 'ตรวจไขว้';
  const rawAuditorDepartmentId = departmentId ?? selectedDepartmentRef?.id ?? null;
  const normalizedAuditorDepartmentId = normalizeDepartmentId(rawAuditorDepartmentId);
  // Normalize auditorDepartmentId to match canonical Firestore document ID casing
  const auditorDepartmentOption = React.useMemo(
    () => {
      if (!normalizedAuditorDepartmentId) return null;
      return (
        departments.find((dept) => normalizeDepartmentId(dept.id) === normalizedAuditorDepartmentId) ?? null
      );
    },
    [departments, normalizedAuditorDepartmentId],
  );
  // Use the canonical department ID for Firestore paths and comparisons
  const auditorDepartmentId = normalizeDepartmentId(auditorDepartmentOption?.id ?? rawAuditorDepartmentId) || null;
  const currentLofId = React.useMemo(() => {
    const selectedLofId = selectedLofRef?.id?.trim() || null;
    const metadataLofId = submissionMetadata.lofId?.trim() || submissionMetadata.lofRef?.id || null;
    const auditorLofId = auditorDepartmentOption?.lofId?.trim() || null;
    return selectedLofId || metadataLofId || auditorLofId;
  }, [auditorDepartmentOption?.lofId, selectedLofRef?.id, submissionMetadata.lofId, submissionMetadata.lofRef]);
  const crossAuditDepartmentOptions = React.useMemo(() => {
    const homeNorm = auditorDepartmentId ? normalizeDepartmentId(auditorDepartmentId) : "";
    const eccOnlyForBloBnkHwn =
      !!isHeadWardNurse &&
      !!homeNorm &&
      HEAD_WARD_NURSE_ECC_ONLY_CROSS_AUDIT_HOME_DEPARTMENT_IDS.some((id) => normalizeDepartmentId(id) === homeNorm);
    let options = getCrossAuditDepartmentOptions({
      departments,
      auditorDepartmentId,
      currentLofId,
      scopedDepartments,
      restrictAuditorPickerToDepartmentIds: eccOnlyForBloBnkHwn
        ? HEAD_WARD_NURSE_CROSS_AUDIT_AUDITOR_DEPARTMENT_IDS
        : null,
    });
    const hideOpdPrimaryPcForEccHwn =
      !!isHeadWardNurse &&
      !!homeNorm &&
      HEAD_WARD_NURSE_CROSS_AUDIT_HIDE_OPD_PRIMARY_PC_FOR_HOME_DEPARTMENT_IDS.some(
        (id) => normalizeDepartmentId(id) === homeNorm,
      );
    if (hideOpdPrimaryPcForEccHwn) {
      options = options.filter((o) => !NURSE_NOTES_UI_EXCLUDED_DEPARTMENT_IDS.has(o.id));
    }
    return options;
  }, [auditorDepartmentId, currentLofId, departments, isHeadWardNurse, scopedDepartments]);

  React.useEffect(() => {
    if (!isNursing) return;
    if (departmentsLoading) return;
    const allowed = new Set(crossAuditDepartmentOptions.map((o) => o.id));
    setCrossAuditByDeptIds((prev) => {
      let changed = false;
      const next = prev.map((id) => {
        const trimmed = id.trim();
        if (!trimmed) return id;
        if (!allowed.has(trimmed)) {
          changed = true;
          return "";
        }
        return id;
      });
      return changed ? next : prev;
    });
  }, [crossAuditDepartmentOptions, departmentsLoading, isNursing]);
  const [rowErrors, setRowErrors] = React.useState<boolean[]>(makeArray(questionCount || 0, () => false));
  const [shiftErrors, setShiftErrors] = React.useState<boolean[]>(makeArray(chartCount, () => false));
  const [fieldErrors, setFieldErrors] = React.useState<{ disease: boolean; focus: boolean; treatments: boolean; hn?: boolean }>({ disease: false, focus: false, treatments: false, hn: false },
  );
  const [supervisionErrors, setSupervisionErrors] = React.useState<SupervisionErrorState>(createSupervisionErrorState());

  const [submitted, setSubmitted] = React.useState<boolean[]>(() => createResetState(chartCount).submitted);
  const [versions, setVersions] = React.useState<number[]>(() => createResetState(chartCount).versions);
  const [history, setHistory] = React.useState<any[][]>(() => createResetState(chartCount).history);
  const [isEditing, setIsEditing] = React.useState<boolean>(true);
  const [incomplete, setIncomplete] = React.useState<boolean[]>(() => createResetState(chartCount).incomplete);

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const submitInProgressRef = React.useRef(false);

  React.useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (submitInProgressRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  const [navOpen, setNavOpen] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [clearOpen, setClearOpen] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState<number | null>(null);
  const [missing, setMissing] = React.useState<string[]>([]);
  const reportingMonth = React.useMemo(() => getReportingMonth(reportingMonthDate), [reportingMonthDate]);
  const isMonthEditable = isEditableMonth(reportingMonth, new Date());
  const isExternallyReadOnly = !!readOnly || !isMonthEditable;
  const [draftSavedAt, setDraftSavedAt] = React.useState<number | null>(null);
  const [persistenceError, setPersistenceError] = React.useState<string | null>(null);
  const [hasLocalEdits, setHasLocalEdits] = React.useState(false);
  const markHasLocalEdits = React.useCallback(() => setHasLocalEdits(true), []);
  const needsFirestorePersistRef = React.useRef(false);
  const scrollTop = () => {
    try {
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      /* no-op */
    }
  };

  React.useEffect(() => {
    if (!card) return;

    const detectedCount = (() => {
      const stored = monthKey ? readDraftWithFallback(monthKey, legacyMonthKey, departmentId).data : null;
      if (!stored) return baseChartCount;
      if (card.title === MED_ERROR_TITLE) {
        return resolveMedErrorVisibleChartCount(stored as Record<string, unknown>, baseChartCount);
      }
      return computeChartCountFromData(stored, baseChartCount);
    })();

    setChartCount(detectedCount);
    const reset = createResetState(detectedCount);
    setChartIndex(0);
    setAnswers(reset.answers);
    setDisease(reset.disease);
    setFocus(reset.focus);
    setTreatments(reset.treatments);
    setProblems(reset.problems);
    setSuggestions(reset.suggestions);
    setShifts(reset.shifts);
    setDates(reset.dates);
    setHn(reset.hn);
      setCrossAuditByDeptIds(reset.crossAuditByDeptIds);
    setSupervisionRecords(reset.supervisionRecords);
    setChartTitleList(reset.chartTitles);
    setRowErrors(makeArray(questionCount || 0, () => false));
    setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
    setShiftErrors(makeArray(detectedCount, () => false));
    setSubmitted(reset.submitted);
    setVersions(reset.versions);
    setHistory(reset.history);
    setIsEditing(true);
    setIncomplete(reset.incomplete);
    setSupervisionErrors(createSupervisionErrorState());
    setHasLocalEdits(false);
  }, [baseChartCount, card, createResetState, departmentId, legacyMonthKey, monthKey, questionCount, hydrationToken]);

  React.useEffect(() => {
    setAnswers((prev) =>
      makeArray(chartCount, (i) => {
        const existing = prev[i];
        if (Array.isArray(existing)) return makeArray(questionCount || 0, (idx) => sanitizeChoice(existing[idx]));
        return makeArray(questionCount || 0, () => DEFAULT_CHOICE);
      }),
    );
    setDisease((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setFocus((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setTreatments((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setProblems((prev) =>
      makeArray(chartCount, (i) => {
        const existing = prev[i];
        if (Array.isArray(existing)) return makeArray(questionCount || 0, (idx) => existing[idx] || "");
        return makeArray(questionCount || 0, () => "");
      }),
    );
    setSuggestions((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setShifts((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setDates((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setHn((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setCrossAuditByDeptIds((prev) => makeArray(chartCount, (i) => prev[i] || ""));
    setSupervisionRecords((prev) => makeArray(chartCount, (i) => prev[i] || createSupervisionRecord()));
    setSubmitted((prev) => makeArray(chartCount, (i) => prev[i] || false));
    setVersions((prev) => makeArray(chartCount, (i) => prev[i] || 0));
    setHistory((prev) => makeArray(chartCount, (i) => prev[i] || []));
    setIncomplete((prev) => makeArray(chartCount, (i) => prev[i] || false));
    setShiftErrors((prev) => makeArray(chartCount, (i) => prev[i] || false));
    setChartTitleList((prev) =>
      isNursing
        ? normalizeNursingChartTitles(prev, chartCount, nursingChartTitleFallback)
        : makeArray(chartCount, (i) => prev[i] || `${periodLabel} ${i + 1}`),
    );
  }, [chartCount, isNursing, nursingChartTitleFallback, periodLabel, questionCount]);

  React.useEffect(() => {
    setSupervisionErrors(createSupervisionErrorState());
  }, [chartIndex, card]);

  React.useEffect(() => {
    setDeleteTarget(null);
  }, [card]);

  const getChartLabel = React.useCallback(
    (idx: number) => chartTitleList[idx] || `${periodLabel} ${idx + 1}`,
    [chartTitleList, periodLabel],
  );
  const getChartTabLabel = React.useCallback(
    (idx: number) => {
      const label = getChartLabel(idx);
      if (getNursingChartTypeFromTitle(label) !== "ตรวจไขว้") return label;
      const match = label.match(/ตรวจไขว้\s*(\d+)/);
      const suffix = match?.[1] ?? `${idx + 1}`;
      return `ตรวจไขว้ ${suffix}`;
    },
    [getChartLabel],
  );

  const askForNursingChartType = React.useCallback((): NursingChartType | null => {
    if (typeof window === 'undefined') return null;
    const promptLines = nursingCrossAuditRequired
      ? 'เลือกประเภท Chart ใหม่ (พิมพ์หมายเลข):\n1) รายโรค\n2) โรคทั่วไป\n3) ตรวจไขว้'
      : 'เลือกประเภท Chart ใหม่ (พิมพ์หมายเลข):\n1) รายโรค\n2) โรคทั่วไป';
    const input = window.prompt(promptLines, '1');
    if (input === null) return null;
    const trimmed = input.trim();
    const mapping: Record<string, NursingChartType> = nursingCrossAuditRequired
      ? { '1': 'รายโรค', '2': 'โรคทั่วไป', '3': 'ตรวจไขว้' }
      : { '1': 'รายโรค', '2': 'โรคทั่วไป' };
    if (mapping[trimmed]) return mapping[trimmed];
    const directMatch = NURSING_CHART_TYPES.find((type) => type === trimmed);
    if (!nursingCrossAuditRequired && directMatch === 'ตรวจไขว้') return null;
    return directMatch || null;
  }, [nursingCrossAuditRequired]);

  React.useEffect(() => {
    if (!card || !monthKey) return;
    if (typeof window === "undefined") return;
    // Defer reading localStorage for nursing until hydration finishes (includes cross-audit dept mirror merge).
    if (hydrationLoading && isNursing) return;
    const s = readDraftWithFallback(monthKey, legacyMonthKey, departmentId).data;
    if (shouldDebugHydration() && card?.title === LAUNCH_CATEGORY_TITLE) {
      console.info('[Form] Read from storage', { monthKey, legacyMonthKey, hasData: !!s });
    }
    if (!s) return;
    try {
      const { answers: storedAnswers, focus: storedFocus, treatments: storedTreatments, chartTitles: storedChartTitles } =
        normalizeSubmissionShape(s as Record<string, unknown>);
      const storedCount = isMedError
        ? resolveMedErrorVisibleChartCount(s as Record<string, unknown>, baseChartCount)
        : computeChartCountFromData(s, baseChartCount);
      // Fallback: use top-level s.focus/s.treatments when normalized arrays are empty (legacy/alternate storage)
      const toStr = (v: unknown) => (v == null ? "" : typeof v === "number" ? String(v) : String(v));
      const rawFocus = Array.isArray(s.focus) ? (s.focus as unknown[]).map(toStr) : [];
      const rawTreatments = Array.isArray(s.treatments) ? (s.treatments as unknown[]).map(toStr) : [];
      const focusSource = storedFocus.some((v) => (v || "").length > 0) ? storedFocus : rawFocus;
      const treatmentsSource = storedTreatments.some((v) => (v || "").length > 0) ? storedTreatments : rawTreatments;
      setChartCount(storedCount);

      setAnswers(
        normalizeStringMatrix(storedAnswers, storedCount, questionCount || 0).map((row) =>
          row.map((val) => sanitizeChoice(val)),
        ),
      );
      setDisease(normalizeArray(s.disease, storedCount, ""));
      setFocus(normalizeArray(focusSource, storedCount, ""));
      setTreatments(normalizeArray(treatmentsSource, storedCount, ""));
      setProblems(normalizeStringMatrix(s.problems, storedCount, questionCount || 0));
      setSuggestions(normalizeArray(s.suggestions, storedCount, ""));
      const storedShifts = Array.isArray(s.shifts)
        ? (s.shifts as any[]).map((v) => {
            if (typeof v === "string") return v;
            if (Array.isArray(v)) return v[0] ?? "";
            return "";
          })
        : [];
      setShifts(normalizeArray(storedShifts, storedCount, ""));
      const storedDates = Array.isArray(s.dates)
        ? (s.dates as any[]).map((d) => (typeof d === "string" ? d : Array.isArray(d) ? d[0] ?? "" : ""))
        : [];
      setDates(normalizeArray(storedDates, storedCount, ""));
      const storedHn = Array.isArray(s.hn)
        ? (s.hn as any[]).map((v) => (typeof v === "string" ? v : Array.isArray(v) ? v[0] ?? "" : ""))
        : [];
      setHn(normalizeArray(storedHn, storedCount, ""));
      const storedCrossAuditByDeptIds =
        Array.isArray((s as any).crossAuditByDeptIds) && (s as any).crossAuditByDeptIds.length > 0
          ? (s as any).crossAuditByDeptIds
          : (s as any).crossAuditTargetDeptIds;
      setCrossAuditByDeptIds(normalizeArray(storedCrossAuditByDeptIds, storedCount, ""));
      const storedTitles = storedChartTitles.filter((v) => typeof v === "string");
      setChartTitleList(
        isNursing
          ? normalizeNursingChartTitles(storedTitles as string[], storedCount, nursingChartTitleFallback)
          : makeArray(storedCount, (i) => (storedTitles[i] as string | undefined) || `${periodLabel} ${i + 1}`),
      );
      setSupervisionRecords(normalizeSupervisionRecords(s.supervisionRecords, storedCount));
      setSubmitted(normalizeArray(s.submitted, storedCount, false));
      setVersions(normalizeArray(s.versions, storedCount, 0));
      setHistory(normalizeArray(s.history, storedCount, []));
      setIncomplete(normalizeArray(s.incomplete, storedCount, false));
      const submissionState = normalizeArray(s.submitted, storedCount, false);
      const firstUn = submissionState.indexOf(false);
      const idx = firstUn >= 0 ? firstUn : 0;
      setChartIndex(idx);
      setIsEditing(!submissionState[idx]);
      setHasLocalEdits(false);
      if (shouldDebugHydration() && card?.title === LAUNCH_CATEGORY_TITLE) {
        console.info('[Form] Populated from storage', { monthKey, chartCount: storedCount });
      }
    } catch (err) {
      if (shouldDebugHydration() && card?.title === LAUNCH_CATEGORY_TITLE) {
        console.warn('[Form] Failed to parse storage data', { monthKey, err });
      }
    }
  }, [baseChartCount, card, departmentId, hydrationLoading, hydrationToken, isMedError, isNursing, legacyMonthKey, monthKey, periodLabel, questionCount]);

  React.useEffect(() => {
    if (!card || !monthKey) return;

    const snapshot: SubmissionDocument = {
      answers,
      disease,
      focus,
      treatments,
      problems,
      suggestions,
      shifts,
      dates,
      hn,
      crossAuditByDeptIds,
      submitted,
      versions,
      history,
      incomplete,
      supervisionRecords,
      chartTitles: chartTitleList,
      categoryTitle: card.title,
      categoryDocId: card.categoryDocId,
    };

    if (!hasLocalEdits) {
      return;
    }

    const shouldSyncFirestore = needsFirestorePersistRef.current;
    if (shouldSyncFirestore) {
      needsFirestorePersistRef.current = false;
      // Run Firestore sync in the same effect turn. Chart delete/add updates many state slices, so this
      // effect often re-runs before a 0ms timer fires; cleanup would cancel the timer and the ref was
      // already cleared — server draft stayed stale and reload resurrected charts.
      writeDraftToLocalStorage(monthKey, snapshot, true);
      if (isMonthEditable) {
        setDraftSavedAt(Date.now());
        setPersistenceError(null);
        const categoryDocId = resolveCategoryDocId(card.title, safeComplianceCategories);
        void persistDepartmentDraft(
          departmentId,
          reportingMonth,
          categoryDocId || card.title,
          { draft: true, data: snapshot, updatedAt: new Date() },
          actorSubmissionMetadata,
        ).catch((err) => {
          console.error('[AutoSave] Firestore persist after chart change failed', err);
          setPersistenceError('ไม่สามารถบันทึกการเปลี่ยนแปลงโครงสร้าง Chart ไปยังเซิร์ฟเวอร์');
        });
      }
    }

    const delayMs = shouldSyncFirestore ? 0 : 750;
    const timer = window.setTimeout(() => {
      writeDraftToLocalStorage(monthKey, snapshot, true);
      if (!isMonthEditable) return;
      if (!shouldSyncFirestore) {
        setDraftSavedAt(Date.now());
        setPersistenceError(null);
      }
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
      // Flush draft on unmount so recomputeFromStorage (run 400ms after close) reads fresh data.
      // Without this, the pending debounced write is cancelled and progress would reset to 0%.
      writeDraftToLocalStorage(monthKey, snapshot, true);
    };
  }, [
    answers,
    card,
    chartTitleList,
    dates,
    disease,
    focus,
    history,
    hn,
    crossAuditByDeptIds,
    incomplete,
    monthKey,
    problems,
    shifts,
    submitted,
    suggestions,
    supervisionRecords,
    treatments,
    versions,
    hasLocalEdits,
    isMonthEditable,
    actorSubmissionMetadata,
    departmentId,
    reportingMonth,
    safeComplianceCategories,
  ]);

  // Push nursing / med_error filled completeness to parent so the sidebar progress bar updates in real-time
  React.useEffect(() => {
    if (
      !card ||
      (card.title !== LAUNCH_CATEGORY_TITLE && card.title !== MED_ERROR_TITLE) ||
      !onNursingFilledCompletenessChange
    ) {
      return;
    }
    const filled = answers.map((row) => hasNursingDraftRowCompleteForProgress(row));
    onNursingFilledCompletenessChange(filled);
  }, [answers, card, onNursingFilledCompletenessChange]);

  const setAnswer = (row: number, v: string) => {
    markHasLocalEdits();
    setAnswers((prev) => {
      const next = prev.map((arr) => arr.slice());
      next[chartIndex][row] = v;
      return next;
    });
  };

  const setProblem = (row: number, v: string) => {
    markHasLocalEdits();
    setProblems((prev) => {
      const next = prev.map((arr) => arr.slice());
      next[chartIndex][row] = v;
      return next;
    });
  };

  const setShift = (v: string) => {
    markHasLocalEdits();
    setShifts((prev) => {
      const next = [...prev];
      next[chartIndex] = v;
      return next;
    });
    setShiftErrors((prev) => {
      const next = [...prev];
      next[chartIndex] = false;
      return next;
    });
  };

  const setDate = (v: string) => {
    markHasLocalEdits();
    setDates((prev) => {
      const next = [...prev];
      next[chartIndex] = v;
      return next;
    });
    if (isSupervision && v) {
      setSupervisionErrors((prev) => ({ ...prev, date: false }));
    }
  };

  const updateSupervisionRecord = (updater: (record: SupervisionRecord) => SupervisionRecord) => {
    markHasLocalEdits();
    setSupervisionRecords((prev) => {
      const next = [...prev];
      const base = prev[chartIndex] || createSupervisionRecord();
      next[chartIndex] = updater(base);
      return next;
    });
  };

  const toggleRecipient = (value: string) => {
    updateSupervisionRecord((record) => {
      const exists = record.recipients.includes(value);
      const recipients = exists ? record.recipients.filter((v) => v !== value) : [...record.recipients, value];
      return { ...record, recipients };
    });
    setSupervisionErrors((prev) => ({ ...prev, recipients: false }));
    if (value === "custom") {
      setSupervisionErrors((prev) => ({ ...prev, recipientsDetail: false }));
    }
  };

  const toggleActivity = (value: string) => {
    updateSupervisionRecord((record) => {
      const exists = record.activities.includes(value);
      const activities = exists ? record.activities.filter((v) => v !== value) : [...record.activities, value];
      return { ...record, activities };
    });
    setSupervisionErrors((prev) => ({ ...prev, activities: false }));
  };

  const toggleIssue = (value: string) => {
    if (isExternallyReadOnly) return;
    updateSupervisionRecord((record) => {
      const exists = record.issues.includes(value);
      const issues = exists ? record.issues.filter((v) => v !== value) : [...record.issues, value];
      return { ...record, issues };
    });
    setSupervisionErrors((prev) => ({ ...prev, issues: false, issueOther: false }));
  };

  const addNewChart = React.useCallback(() => {
    if (isExternallyReadOnly) return;
    if (!departmentId) {
      setPersistenceError(isMedError ? "กรุณาเลือกหน่วยงานก่อนเพิ่มครั้ง" : "กรุณาเลือกหน่วยงานก่อนเพิ่ม Chart");
      return;
    }
    const nextIndex = chartCount;
    const nextCount = chartCount + 1;

    if (isNursing) {
      const selectedType = askForNursingChartType();
      if (!selectedType) return;
      markHasLocalEdits();
      needsFirestorePersistRef.current = true;
      setPersistenceError(null);
      setChartTitleList((prev) =>
        normalizeNursingChartTitles(
          [...prev, getNextNursingChartTitle(selectedType, prev)],
          nextCount,
          nursingChartTitleFallback,
        ),
      );
    } else {
      markHasLocalEdits();
      needsFirestorePersistRef.current = true;
      setPersistenceError(null);
      setChartTitleList((prev) => makeArray(nextCount, (i) => prev[i] || `${periodLabel} ${i + 1}`));
    }

    setChartCount(nextCount);
    setChartIndex(nextIndex);
    setIsEditing(true);
    setRowErrors(makeArray(questionCount || 0, () => false));
    setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
    setShiftErrors((prev) => makeArray(nextCount, (i) => prev[i] || false));
    setIncomplete((prev) => makeArray(nextCount, (i) => prev[i] || false));
    setCrossAuditByDeptIds((prev) => makeArray(nextCount, (i) => prev[i] || ""));
    setSupervisionErrors(createSupervisionErrorState());
    setTimeout(scrollTop, 0);
  }, [askForNursingChartType, chartCount, isExternallyReadOnly, isMedError, isNursing, markHasLocalEdits, periodLabel, questionCount]);

  const allowChartDeletion = allowsExtraComplianceCharts(card?.title);
  const minChartsRequired = requiredComplianceChartSlots(card?.title, departmentId);

  const deleteChart = React.useCallback(
    (index: number) => {
      if (isExternallyReadOnly) return;
      if (!allowChartDeletion) return;
      if (!departmentId) {
        setPersistenceError("กรุณาเลือกหน่วยงานก่อนลบ Chart");
        return;
      }
      if (index < minChartsRequired) return;
      if (chartCount <= minChartsRequired) return;

      markHasLocalEdits();
      needsFirestorePersistRef.current = true;
      setPersistenceError(null);
      const nextCount = Math.max(minChartsRequired, chartCount - 1);
      if (nextCount === chartCount) return;

      const removeAtIndex = <T,>(arr: T[]): T[] => arr.filter((_, idx) => idx !== index);

      setChartCount(nextCount);
      setAnswers((prev) => removeAtIndex(prev));
      setDisease((prev) => removeAtIndex(prev));
      setFocus((prev) => removeAtIndex(prev));
      setTreatments((prev) => removeAtIndex(prev));
      setProblems((prev) => removeAtIndex(prev));
      setSuggestions((prev) => removeAtIndex(prev));
      setShifts((prev) => removeAtIndex(prev));
      setDates((prev) => removeAtIndex(prev));
      setHn((prev) => removeAtIndex(prev));
      setCrossAuditByDeptIds((prev) => removeAtIndex(prev));
      setSupervisionRecords((prev) => removeAtIndex(prev));
      setChartTitleList((prev) =>
        isNursing
          ? normalizeNursingChartTitles(removeAtIndex(prev), nextCount, nursingChartTitleFallback)
          : removeAtIndex(prev),
      );
      let nextSubmitted: boolean[] = [];
      setSubmitted((prev) => {
        nextSubmitted = removeAtIndex(prev);
        return nextSubmitted;
      });
      setVersions((prev) => removeAtIndex(prev));
      setHistory((prev) => removeAtIndex(prev));
      setIncomplete((prev) => removeAtIndex(prev));
      setShiftErrors((prev) => removeAtIndex(prev));
      setSupervisionErrors(createSupervisionErrorState());

      const nextIndex = (() => {
        if (chartIndex > index) return chartIndex - 1;
        if (chartIndex === index) return Math.max(0, Math.min(index, nextCount - 1));
        return chartIndex;
      })();

      setChartIndex(nextIndex);
      setIsEditing(!nextSubmitted[nextIndex]);
      setTimeout(scrollTop, 0);
    },
    [
      allowChartDeletion,
      chartCount,
      chartIndex,
      departmentId,
      isExternallyReadOnly,
      isNursing,
      markHasLocalEdits,
      minChartsRequired,
    ],
  );

  const currentSupervisionRecord = supervisionRecords[chartIndex] || createSupervisionRecord();

  const totals = React.useMemo(() => {
    const rowVals = answers[chartIndex] || [];
    const { score, full, pct } = completenessFromUnknownRow(rowVals);
    return { score, total: full, pct };
  }, [answers, chartIndex]);

  const monthlyTotals = React.useMemo(() => {
    let score = 0;
    let full = 0;
    answers.forEach((chart) => {
      const s = completenessFromUnknownRow(chart);
      score += s.score;
      full += s.full;
    });
    const pct = full ? Math.round((score / full) * 10000) / 100 : 0;
    return { score, total: full, pct };
  }, [answers]);

  const consistencyPct = React.useMemo(() => {
    const { focus: fr, treatments: tr } = resolveNursingFocusTreatmentsForOR(
      focus[chartIndex] || "",
      treatments[chartIndex] || "",
      isORDepartment,
    );
    const p = perChartConsistencyPctFromStrings(fr, tr);
    return p ?? 0;
  }, [focus, treatments, chartIndex, isORDepartment]);

  /** Unweighted mean of per-chart consistency where treatments &gt; 0; null if none qualify. */
  const overallNursingConsistencyPct = React.useMemo(() => {
    if (!requiresClinicalInputs) return null;
    const samples: Array<number | null> = [];
    for (let i = 0; i < chartCount; i += 1) {
      const { focus: fr, treatments: tr } = resolveNursingFocusTreatmentsForOR(
        focus[i] || "",
        treatments[i] || "",
        isORDepartment,
      );
      samples.push(perChartConsistencyPctFromStrings(fr, tr));
    }
    return overallConsistencyUnweightedAverage(samples);
  }, [requiresClinicalInputs, chartCount, focus, treatments, isORDepartment]);

  const hasDraft = React.useCallback(
    (i: number) => {
      const a = answers[i] || [];
      if (a.some((v) => hasNonDefaultChoice(v))) return true;
      if ((disease[i] || "").length) return true;
      if ((focus[i] || "").length) return true;
      if ((treatments[i] || "").length) return true;
      if ((suggestions[i] || "").length) return true;
      const p = problems[i] || [];
      if (p.some((v) => v && v.length > 0)) return true;
      if ((shifts[i] || "").length) return true;
      if ((dates[i] || "").length) return true;
      if ((hn[i] || "").length) return true;
      if (isSupervision) {
        const record = supervisionRecords[i] || createSupervisionRecord();
        if (record.recipients.length) return true;
        if (record.recipientsDetail.trim().length) return true;
        if (record.activities.length) return true;
        if (record.issues.length) return true;
        if (record.issueOther.trim().length) return true;
        if (record.problemDetail.trim().length) return true;
        if (record.solution.trim().length) return true;
        if (record.followUp.trim().length) return true;
      }
      return false;
    },
    [answers, disease, focus, treatments, suggestions, problems, shifts, dates, hn, isSupervision, supervisionRecords],
  );
  const validate = () => {
    const rowChoiceErrs = (answers[chartIndex] || []).map((v) => !(v === "1" || v === "0" || v === "NA"));
    const shiftErr =
      requiresShiftSelection &&
      !(shifts[chartIndex] === "morning" || shifts[chartIndex] === "evening" || shifts[chartIndex] === "night");
    let diseaseErr = false,
      focusErr = false,
      treatErr = false,
      hnErr = false;
    if (requiresClinicalInputs && !isORDepartment) {
      diseaseErr = !disease[chartIndex] || !disease[chartIndex].trim();
      const f = Number(focus[chartIndex] || "");
      const t = Number(treatments[chartIndex] || "");
      focusErr = !(f > 0);
      treatErr = !(t > 0);
    }
    if (requiresHN) {
      hnErr = !hn[chartIndex] || !hn[chartIndex].trim();
    }
    let supervisionErrFlags = createSupervisionErrorState();
    if (isSupervision) {
      const record = supervisionRecords[chartIndex] || createSupervisionRecord();
      supervisionErrFlags = {
        recipients: record.recipients.length === 0,
        recipientsDetail: record.recipients.includes("custom") && !record.recipientsDetail.trim(),
        activities: record.activities.length === 0,
        issues: record.issues.length === 0,
        issueOther: record.issues.includes("other") && !record.issueOther.trim(),
        problemDetail: !record.problemDetail.trim(),
        solution: !record.solution.trim(),
        followUp: !record.followUp.trim(),
        date: !(dates[chartIndex] && dates[chartIndex].length > 0),
      };
    }
    const requireRemarksOnZero = card?.title === MED_ERROR_TITLE;
    const remarkErrs = requireRemarksOnZero
      ? (answers[chartIndex] || []).map((v, idx) => {
          if (v !== '0') return false;
          const note = (problems[chartIndex] || [])[idx] || '';
          return !note.trim();
        })
      : (answers[chartIndex] || []).map(() => false);
    const remarksInvalid =
      requireRemarksOnZero &&
      !medErrorRemarksValidForWeek(answers[chartIndex], problems[chartIndex]);
    const hasErr =
      rowChoiceErrs.some(Boolean) ||
      remarkErrs.some(Boolean) ||
      remarksInvalid ||
      shiftErr ||
      diseaseErr ||
      focusErr ||
      treatErr ||
      hnErr ||
      (isSupervision && Object.values(supervisionErrFlags).some(Boolean));
    setRowErrors(rowChoiceErrs.map((choiceErr, idx) => choiceErr || remarkErrs[idx]));
    setShiftErrors((prev) => {
      const next = [...prev];
      next[chartIndex] = shiftErr;
      return next;
    });
    setFieldErrors({ disease: diseaseErr, focus: focusErr, treatments: treatErr, hn: hnErr });
    if (isSupervision) {
      setSupervisionErrors(supervisionErrFlags);
    }

    const missingList: string[] = [];
    rowChoiceErrs.forEach((e, idx) => {
      if (e) missingList.push(`ยังไม่ได้เลือก 1/0/NA: ${flatList[idx]}`);
    });
    remarkErrs.forEach((e, idx) => {
      if (e) missingList.push(`ต้องบันทึกเหตุผลเมื่อเลือก 0: ${flatList[idx]}`);
    });
    if (shiftErr) missingList.push("ยังไม่ได้เลือกเวร");
    if (requiresClinicalInputs && !isORDepartment) {
      if (diseaseErr) missingList.push("ยังไม่ได้กรอก: โรค");
      if (focusErr) missingList.push("ยังไม่ได้กรอก: จำนวน Focus list ที่สอดคล้องกับการรักษาของแพทย์ (> 0)");
      if (treatErr) missingList.push("ยังไม่ได้กรอก: จำนวนการรักษาที่แพทย์บันทึก (> 0)");
    }
    if (requiresHN && hnErr) missingList.push("ยังไม่ได้กรอก: HN");
    if (isSupervision) {
      if (supervisionErrFlags.date) missingList.push("ยังไม่ได้เลือกวันที่");
      if (supervisionErrFlags.recipients) missingList.push("ยังไม่ได้เลือกผู้รับการนิเทศ");
      if (supervisionErrFlags.recipientsDetail) missingList.push("โปรดระบุผู้รับการนิเทศ");
      if (supervisionErrFlags.activities) missingList.push("ยังไม่ได้เลือกกิจกรรมการนิเทศ");
      if (supervisionErrFlags.issues) missingList.push("ยังไม่ได้เลือกประเด็นปัญหา");
      if (supervisionErrFlags.issueOther) missingList.push("โปรดระบุประเด็นปัญหาอื่นๆ");
      if (supervisionErrFlags.problemDetail) missingList.push("ยังไม่ได้กรอกสรุปรายละเอียดของปัญหา");
      if (supervisionErrFlags.solution) missingList.push("ยังไม่ได้กรอกแนวทางแก้ไข");
      if (supervisionErrFlags.followUp) missingList.push("ยังไม่ได้กรอกแผนการติดตามผล");
    }
    return { hasErr, missingList };
  };

  const finalizeSubmit = async (incompleteFlag: boolean, mode: "stay" | "next") => {
    try {
      const effectiveDepartmentId = departmentId ?? selectedDepartmentRef?.id ?? null;
      const categoryTitle = card?.title ?? "";
      const categoryDocId = resolveCategoryDocId(categoryTitle, safeComplianceCategories);
      const isORDept = !!effectiveDepartmentId && normalizeDepartmentId(effectiveDepartmentId) === "OR";

      if (isWardSubmitRetired({ categoryDocId, title: categoryTitle })) {
        const displayTitle =
          resolveWardSubmitDisplayTitle({ categoryDocId, title: categoryTitle }) ??
          (categoryTitle.trim() || 'ไม่ทราบชื่อหมวด');
        setPersistenceError(formatWardSubmitClosedMessage(wardSubmitSubmitClosedMessage(displayTitle)));
        return;
      }

      if (!requireLofRef && !effectiveDepartmentId) {
        setPersistenceError("ไม่พบแผนก (department) ของผู้ใช้ จึงไม่สามารถส่งข้อมูลได้");
        return;
      }

      const diseaseResolved =
        isORDept && (!disease[chartIndex] || !disease[chartIndex].trim()) ? "NA" : (disease[chartIndex] || "");
      const focusResolved =
        isORDept && !(Number(focus[chartIndex] || 0) > 0) ? "NA" : (focus[chartIndex] || "");
      const treatmentsResolved =
        isORDept && !(Number(treatments[chartIndex] || 0) > 0) ? "NA" : (treatments[chartIndex] || "");

      const snap = {
        chart: chartIndex + 1,
        title: categoryTitle,
        categoryTitle,
        categoryDocId,
        criteria: flatList,
        answers: answers[chartIndex],
        shifts: shifts[chartIndex],
        disease: diseaseResolved,
        focus: focusResolved,
        treatments: treatmentsResolved,
        consistencyPct,
        hn: hn[chartIndex],
        problems: problems[chartIndex],
        suggestions: suggestions[chartIndex],
        dates: dates[chartIndex],
        totals,
        submittedAt: new Date().toISOString(),
        supervisionRecord: supervisionRecords[chartIndex],
      };

      const newHistory = history.map((a) => a.slice());
      newHistory[chartIndex] = [...(newHistory[chartIndex] || []), snap];
      const newVersions = [...versions];
      newVersions[chartIndex] = (newVersions[chartIndex] || 0) + 1;
      const newSubmitted = [...submitted];
      newSubmitted[chartIndex] = true;
      const newIncomplete = [...incomplete];
      newIncomplete[chartIndex] = incompleteFlag;

      const diseaseForSnapshot = [...disease];
      diseaseForSnapshot[chartIndex] = diseaseResolved;
      const focusForSnapshot = [...focus];
      focusForSnapshot[chartIndex] = focusResolved;
      const treatmentsForSnapshot = [...treatments];
      treatmentsForSnapshot[chartIndex] = treatmentsResolved;

      const finalSnapshot: SubmissionDocument = {
        answers,
        disease: diseaseForSnapshot,
        focus: focusForSnapshot,
        treatments: treatmentsForSnapshot,
        problems,
        suggestions,
        shifts,
        dates,
        hn,
        crossAuditByDeptIds,
        submitted: newSubmitted,
        versions: newVersions,
        history: newHistory,
        incomplete: newIncomplete,
        supervisionRecords,
        chartTitles: chartTitleList,
        categoryTitle: categoryTitle || null,
        categoryId: categoryDocId || null,
        categoryDocId: categoryDocId || null,
      };
      if (shouldDebugHydration()) {
        console.info("[SubmitDebug] payload counters", compactSubmitCounters(finalSnapshot, chartIndex));
      }

      const applySubmissionState = () => {
        writeDraftToLocalStorage(monthKey, finalSnapshot, false);
        setDraftSavedAt(Date.now());
        setPersistenceError(null);
        setHistory(newHistory);
        setVersions(newVersions);
        setSubmitted(newSubmitted);
        setIncomplete(newIncomplete);
        setIsEditing(false);
        setSupervisionErrors(createSupervisionErrorState());
        setHasLocalEdits(false);
      };

      const finalizeAfterSubmit = () => {
        const sanitizedChangeSet = {
          title: snap.title ?? null,
          items: sanitizeStringArray(snap.items),
          choices: sanitizeStringArray(snap.choices),
          answers: sanitizeStringArray(snap.answers),
          focus: snap.focus ?? "",
          shifts: snap.shifts ?? "",
          disease: snap.disease ?? "",
          hn: snap.hn ?? "",
          treatments: snap.treatments ?? "",
          consistencyPct: snap.consistencyPct ?? 0,
          problems: sanitizeStringArray(snap.problems),
          suggestions: snap.suggestions ?? "",
          dates: snap.dates ?? "",
          totals: snap.totals ?? { score: 0, total: 0, pct: 0 },
          submittedAt: snap.submittedAt ?? null,
          supervisionRecord: sanitizeSupervisionRecord(snap.supervisionRecord),
        } as const;

        void logAuditEvent({
          userId: currentUserId ?? null,
          resource: 'compliance',
          action: 'edit',
          allowed: true,
          resourceId: effectiveDepartmentId ?? null,
          metadata: {
            reportingMonth: reportingMonth ?? null,
            category: card?.title ?? null,
            chartIndex: chartIndex + 1,
            version: newVersions[chartIndex],
            incomplete: incompleteFlag,
            submittedAt: snap.submittedAt,
            changeSet: sanitizedChangeSet,
          },
        });

        if (mode === "next") {
          const next = Math.min(chartCount - 1, chartIndex + 1);
          setChartIndex(next);
          setIsEditing(!newSubmitted[next]);
          setRowErrors(makeArray(questionCount || 0, () => false));
          setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
          setShiftErrors(makeArray(chartCount, () => false));
        }
        setTimeout(scrollTop, 0);
      };

      if (isCrossAuditChart) {
        // Simplified model: user submits their own department's data (same as regular charts).
        // Dropdown "ตรวจไขว้โดย" is metadata only - notes which department performed the cross-audit.
        const effectiveDeptId = effectiveDepartmentId ?? auditorDepartmentId;
        const crossAuditPerformedByDeptId = normalizeDepartmentId(crossAuditByDeptIds[chartIndex]) || null;
        const slotMatch = currentChartTitle.match(/ตรวจไขว้\s+(\d+)/);
        const parsedSlot = Number.parseInt(slotMatch?.[1] ?? "", 10);
        const crossAuditSlot = Number.isFinite(parsedSlot) ? parsedSlot : chartIndex + 1;
        const performedByDepartment =
          departments.find((dept) => normalizeDepartmentId(dept.id) === crossAuditPerformedByDeptId) ?? null;
        const performedByDeptIdForFirestore =
          performedByDepartment?.id ?? crossAuditPerformedByDeptId ?? "unknown";
        const crossAuditCategoryKey = `CROSS_AUDIT_NURSING__${performedByDeptIdForFirestore}**${crossAuditSlot}`;

        if (!effectiveDeptId && !suppressDepartmentValidation) {
          setPersistenceError("กรุณาเลือกแผนกก่อนส่งข้อมูล");
          return;
        }
        if (!crossAuditPerformedByDeptId) {
          setPersistenceError("กรุณาเลือกหน่วยงานที่มาตรวจไขว้ (ตรวจไขว้โดย)");
          return;
        }
        if (normalizeDepartmentId(effectiveDeptId) === crossAuditPerformedByDeptId) {
          setPersistenceError("ห้ามตรวจไขว้หน่วยงานของตนเอง");
          return;
        }

        const crossAuditTitle = currentChartTitle || snap.title;
        const crossAuditPayload: SubmissionDocument = {
          ...snap,
          chartTitles: [crossAuditTitle],
          answers: [snap.answers],
          focus: [snap.focus],
          treatments: [snap.treatments],
          criteria: sanitizeStringArray(snap.criteria),
          choices: sanitizeStringArray((snap as { choices?: string[] }).choices),
          totals: snap.totals,
          crossAudit: true,
          crossAuditSlot,
          /** Audited ward (where the chart lives); user-path hydration matches this, not only `departmentId`. */
          auditedDepartmentId: effectiveDeptId ?? null,
          sourceCategoryId: categoryDocId ?? snap.categoryDocId ?? null,
          sourceCategoryTitle: categoryTitle || snap.categoryTitle || null,
          auditorDepartmentId: performedByDeptIdForFirestore,
          auditorDepartmentName: performedByDepartment?.name ?? performedByDeptIdForFirestore ?? null,
          title: crossAuditTitle,
        };

        if (onSubmitChart) onSubmitChart(chartIndex, incompleteFlag);
        try {
          submitInProgressRef.current = true;
          await clearDepartmentDraft(effectiveDeptId, reportingMonth, crossAuditCategoryKey, crossAuditPayload, {
            ...actorSubmissionMetadata,
            departmentId: effectiveDeptId ?? actorSubmissionMetadata.departmentId ?? null,
            departmentRef:
              actorSubmissionMetadata.departmentRef ??
              (effectiveDeptId ? doc(db, "departments", effectiveDeptId) : null),
            lofRef: selectedLofRef ?? actorSubmissionMetadata.lofRef ?? submissionMetadata.lofRef ?? null,
            submittedAt: new Date(),
            updatedAt: new Date(),
          });
          if (import.meta.env.DEV) {
            console.info("[CrossAudit] wrote under own department", {
              departmentId: effectiveDeptId,
              crossAuditPerformedBy: performedByDeptIdForFirestore,
              reportingMonth,
              crossAuditCategoryKey,
            });
          }
          applySubmissionState();

          // Sync submitted[] and crossAuditByDeptIds to the parent nursing doc in Firestore.
          // Without this, hydration on reload reads stale state (cross-audit slots = false,
          // auditor department empty) because clearDepartmentDraft only writes the
          // cross-audit doc, not the nursing doc. Uses updateDoc with setDoc(merge) fallback
          // when the parent doc does not exist yet — see ../lib/parentNursingSync.ts.
          if (currentUserId && effectiveDeptId && (categoryDocId || categoryTitle)) {
            // Must match prepareDepartmentSubmissionPayloads / clearDepartmentDraft: canonical month + same
            // category segment as nurse_notes submit (`categoryDocId || categoryTitle`), or updateDoc misses
            // and reload loses submitted[] / crossAuditByDeptIds on the parent doc.
            const monthCanon = canonicalReportingMonth(reportingMonth, reportingMonth);
            const nursingCategorySegment =
              resolveCanonicalCategoryId(categoryDocId || categoryTitle) ??
              (categoryDocId || categoryTitle).trim();
            if (!nursingCategorySegment.includes("/")) {
            const scopeId = (effectiveDeptId ?? actorSubmissionMetadata.departmentId ?? "").trim();
            const nursingDocId = `${currentUserId}_${scopeId}_${monthCanon}_${nursingCategorySegment}`;
            const nursingRef = doc(db, "users", currentUserId, "submissions", nursingDocId);
            const syncPayload: ParentNursingSyncPayload = { submitted: newSubmitted, crossAuditByDeptIds };
            const logParentSyncTelemetry = (event: ParentNursingSyncTelemetryEvent) => {
              if (typeof window === "undefined") return;
              try {
                if (localStorage.getItem("debugHydration") !== "1") return;
              } catch {
                return;
              }
              if (event.outcome === "failed") {
                console.info("[CrossAudit] parent nursing sync failed", {
                  context: event.context,
                  phase: event.phase,
                  code: getFirestoreErrorCode(event.err),
                });
              } else {
                console.info("[CrossAudit] parent nursing sync", {
                  context: event.context,
                  outcome: event.outcome,
                });
              }
            };
            try {
              if (typeof window !== "undefined" && localStorage.getItem("debugHydration") === "1") {
                const canonicalForUserPath =
                  resolveCanonicalCategoryId(nursingCategorySegment) ?? nursingCategorySegment.trim();
                console.info("[CrossAudit] parent nursing doc id segments (compare to nurse_notes submit)", {
                  scopeId,
                  monthCanon,
                  parentSyncCategorySegment: nursingCategorySegment,
                  hydrationDirectFetchCategorySegment: canonicalForUserPath,
                  segmentDiffersFromHydrationFallback: nursingCategorySegment !== canonicalForUserPath,
                });
              }
            } catch {
              /* ignore localStorage / logging */
            }
            void syncParentNursingSubmittedFields(nursingRef, syncPayload, "user-submissions", logParentSyncTelemetry);
            const fsDeptId = getFirestorePathDepartmentId(effectiveDeptId);
            if (fsDeptId) {
              const deptRef = doc(db, "submissions", fsDeptId, monthCanon, nursingCategorySegment);
              void syncParentNursingSubmittedFields(deptRef, syncPayload, "dept-submissions", logParentSyncTelemetry);
            }
            }
          }
        } catch (error) {
          if (onSubmitChart) onSubmitChart(chartIndex, incompleteFlag, true);
          const compactError = compactErrorSummary(error);
          console.error("Failed to submit cross-audit compliance chart", {
            ...compactError,
            counters: compactSubmitCounters(finalSnapshot, chartIndex),
          });
          setPersistenceError(`Submit failed: ${compactError.errorCode ?? "unknown"} - ${compactError.errorMessage}`);
          return;
        } finally {
          submitInProgressRef.current = false;
        }

        finalizeAfterSubmit();
        return;
      }

      if (requireLofRef && !selectedLofRef) {
        setPersistenceError("กรุณาเลือกหน่วยงานนิเทศก่อนส่งข้อมูล");
        return;
      }

      if (!effectiveDepartmentId && !suppressDepartmentValidation) {
        setPersistenceError("กรุณาเลือกแผนกก่อนส่งข้อมูล");
        return;
      }

      const resolvedDepartmentRef =
        actorSubmissionMetadata.departmentRef ??
        (effectiveDepartmentId ? doc(db, "departments", effectiveDepartmentId) : null);
      const resolvedLofRef =
        actorSubmissionMetadata.lofRef ??
        (actorSubmissionMetadata.lofId ? doc(db, "lofs", actorSubmissionMetadata.lofId) : null);

      if (onSubmitChart) onSubmitChart(chartIndex, incompleteFlag);
      try {
        submitInProgressRef.current = true;
        await clearDepartmentDraft(
          requireLofRef ? null : effectiveDepartmentId,
          reportingMonth,
          categoryDocId || categoryTitle,
          finalSnapshot,
          {
            ...actorSubmissionMetadata,
            departmentId: effectiveDepartmentId ?? actorSubmissionMetadata.departmentId ?? null,
            departmentRef: resolvedDepartmentRef,
            lofRef: resolvedLofRef,
            submittedAt: new Date(),
            updatedAt: new Date(),
          },
        );
        applySubmissionState();
      } catch (error) {
        if (onSubmitChart) onSubmitChart(chartIndex, incompleteFlag, true);
        const compactError = compactErrorSummary(error);
        console.error("Failed to submit compliance chart", {
          ...compactError,
          counters: compactSubmitCounters(finalSnapshot, chartIndex),
        });
        setPersistenceError(`Submit failed: ${compactError.errorCode ?? "unknown"} - ${compactError.errorMessage}`);
        return;
      } finally {
        submitInProgressRef.current = false;
      }

      finalizeAfterSubmit();
    } catch (error) {
      const compactError = compactErrorSummary(error);
      console.error("Failed to submit compliance chart", compactError);
      setPersistenceError(`Submit failed: ${compactError.errorCode ?? "unknown"} - ${compactError.errorMessage}`);
    }
  };

  const submitCurrentChart = async (mode: "stay" | "next" = "stay") => {
    if (isExternallyReadOnly) {
      setMissing(["ไม่สามารถส่งข้อมูลได้เพราะปิดรับการแก้ไขสำหรับรอบเดือนนี้"]);
      setConfirmOpen(true);
      return;
    }
    if (!isCategoryAllowed()) {
      setMissing([`ไม่สามารถบันทึกข้อมูลสำหรับหมวดหมู่ ${card?.title ?? ''}`]);
      setConfirmOpen(true);
      return;
    }
    const v = validate();
    if (v.hasErr) {
      setMissing(v.missingList);
      setConfirmOpen(true);
      return;
    }
    try {
      await finalizeSubmit(false, mode);
    } catch (error) {
      const compactError = compactErrorSummary(error);
      console.error("Failed to submit compliance chart", compactError);
      setPersistenceError(`Submit failed: ${compactError.errorCode ?? "unknown"} - ${compactError.errorMessage}`);
    }
  };

  const startEdit = () => {
    if (isExternallyReadOnly) return;
    setIsEditing(true);
  };
  const saveResubmission = async () => {
    if (isExternallyReadOnly) {
      setMissing(["ไม่สามารถบันทึกได้เพราะปิดรับการแก้ไขสำหรับรอบเดือนนี้"]);
      setConfirmOpen(true);
      return;
    }
    if (!isCategoryAllowed()) {
      setMissing([`ไม่สามารถบันทึกข้อมูลสำหรับหมวดหมู่ ${card?.title ?? ''}`]);
      setConfirmOpen(true);
      return;
    }
    const v = validate();
    if (v.hasErr) {
      setMissing(v.missingList);
      setConfirmOpen(true);
      return;
    }
    try {
      await finalizeSubmit(false, "stay");
    } catch (error) {
      const compactError = compactErrorSummary(error);
      console.error("Failed to submit compliance chart", compactError);
      setPersistenceError(`Submit failed: ${compactError.errorCode ?? "unknown"} - ${compactError.errorMessage}`);
    }
  };

  const clearAllInputs = async () => {
    if (isExternallyReadOnly) return;
    const effectiveDepartmentId = departmentId ?? selectedDepartmentRef?.id ?? null;
    const category = card
      ? (resolveCategoryDocId(card.title, safeComplianceCategories) || card.title)
      : "";
    if (shouldDebugHydration()) {
      const firestoreDeptId = effectiveDepartmentId ? getFirestorePathDepartmentId(effectiveDepartmentId) : "";
      console.info("[ClearAll] Targeting legacy mark", {
        effectiveDepartmentId,
        category,
        reportingMonth,
        docPath: firestoreDeptId && category ? `submissions/${firestoreDeptId}/${reportingMonth}/${category}` : null,
      });
    }
    if (effectiveDepartmentId && category) {
      try {
        await markSubmissionAsLegacy(
          effectiveDepartmentId,
          reportingMonth,
          category,
          currentUserId,
          actorSubmissionMetadata,
        );
        onReportingTableRefresh?.();
      } catch (err) {
        const errorDetails =
          err && typeof err === "object"
            ? {
                code: "code" in err ? (err as { code?: unknown }).code : null,
                message: "message" in err ? (err as { message?: unknown }).message : null,
              }
            : { code: null, message: null };
        const code = typeof errorDetails.code === "string" ? errorDetails.code : null;
        const message = typeof errorDetails.message === "string" ? errorDetails.message : null;
        console.error(
          `[ClearAll] Failed to mark submission as legacy | code=${code ?? "null"} | message=${message ?? "null"} | effectiveDepartmentId=${effectiveDepartmentId ?? "null"} | category=${category || "null"} | reportingMonth=${reportingMonth}`,
        );
        setPersistenceError("ไม่สามารถทำเครื่องหมายข้อมูลเก่าได้ กรุณาลองใหม่");
        return;
      }
    }
    const reset = createResetState(chartCount);
    setAnswers(reset.answers);
    setDisease(reset.disease);
    setFocus(reset.focus);
    setTreatments(reset.treatments);
    setProblems(reset.problems);
    setSuggestions(reset.suggestions);
    setShifts(reset.shifts);
    setDates(reset.dates);
    setHn(reset.hn);
    setCrossAuditByDeptIds(reset.crossAuditByDeptIds);
    setSupervisionRecords(reset.supervisionRecords);
    setSubmitted(reset.submitted);
    setVersions(reset.versions);
    setHistory(reset.history);
    setIncomplete(reset.incomplete);
    setChartTitleList(reset.chartTitles);
    setRowErrors(makeArray(questionCount || 0, () => false));
    setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
    setShiftErrors(makeArray(chartCount, () => false));
    setSupervisionErrors(createSupervisionErrorState());
    setMissing([]);
    setConfirmOpen(false);
    setNavOpen(false);
    setChartIndex(0);
    setIsEditing(true);
    setHasLocalEdits(false);
    try {
      if (monthKey) localStorage.removeItem(monthKey);
      if (legacyMonthKey && legacyMonthKey !== monthKey) localStorage.removeItem(legacyMonthKey);
    } catch {
      /* ignore storage errors */
    }
    onSubmitChart?.(0, true);
    scrollTop();
  };

  const crossAuditSlotResolved = React.useMemo(() => {
    const m = currentChartTitle.match(/ตรวจไขว้\s*(\d+)/);
    const parsed = m ? Number.parseInt(m[1], 10) : NaN;
    return Number.isFinite(parsed) ? parsed : chartIndex + 1;
  }, [currentChartTitle, chartIndex]);

  const crossAuditFirestorePerformerId = React.useMemo(() => {
    const raw = normalizeDepartmentId(crossAuditByDeptIds[chartIndex] || "") || null;
    if (!raw) return null;
    const match = departments.find((dept) => normalizeDepartmentId(dept.id) === raw) ?? null;
    return match?.id ?? crossAuditByDeptIds[chartIndex]?.trim() ?? null;
  }, [chartIndex, crossAuditByDeptIds, departments]);

  const applyCrossAuditRemoteSlice = React.useCallback(
    (crossDoc: SubmissionDocument, resolvedChartIndex: number) => {
      if (submitInProgressRef.current) return;
      enrichCrossAuditDocFromMetrics(crossDoc);
      const patch = crossAuditSliceToRowPatch(resolvedChartIndex, crossDoc);
      if (!patch) return;
      const idx = patch.chartIndex;
      if (patch.answersRow) {
        setAnswers((prev) => {
          const next = prev.map((row) => [...row]);
          while (next.length <= idx) next.push([]);
          next[idx] = patch.answersRow!.map((c) => sanitizeChoice(c));
          return next;
        });
      }
      if (patch.focus !== undefined) {
        setFocus((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.focus ?? "";
          return next;
        });
      }
      if (patch.treatments !== undefined) {
        setTreatments((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.treatments ?? "";
          return next;
        });
      }
      if (patch.disease !== undefined) {
        setDisease((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.disease ?? "";
          return next;
        });
      }
      if (patch.dates !== undefined) {
        setDates((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.dates ?? "";
          return next;
        });
      }
      if (patch.shifts !== undefined) {
        setShifts((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.shifts ?? "";
          return next;
        });
      }
      if (patch.hn !== undefined) {
        setHn((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.hn ?? "";
          return next;
        });
      }
      if (patch.suggestions !== undefined) {
        setSuggestions((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push("");
          next[idx] = patch.suggestions ?? "";
          return next;
        });
      }
      if (patch.problems) {
        setProblems((prev) => {
          const next = prev.map((row) => [...row]);
          while (next.length <= idx) next.push([]);
          next[idx] = [...patch.problems!];
          return next;
        });
      }
      if (patch.submitted) {
        setSubmitted((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push(false);
          next[idx] = true;
          return next;
        });
        setIsEditing(false);
      }
      if (patch.version != null) {
        setVersions((prev) => {
          const next = [...prev];
          while (next.length <= idx) next.push(0);
          next[idx] = patch.version!;
          return next;
        });
      }
    },
    [],
  );

  const readonly = (submitted[chartIndex] && !isEditing) || isExternallyReadOnly;
  const activeChartLabel = getChartTabLabel(chartIndex);

  const fillCurrentChartAnswers = (choice: BulkChoice) => {
    if (readonly) return;
    markHasLocalEdits();
    setAnswers((prev) => {
      const next = prev.map((arr) => arr.slice());
      const len = Math.max(questionCount, next[chartIndex]?.length ?? 0);
      next[chartIndex] = fillAnswersRow(len, choice);
      return next;
    });
    setRowErrors((prev) => prev.map(() => false));
  };

  return (
    <AnimatePresence>
      {card && (
        <motion.aside
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: "100%", opacity: 0 }}
          transition={{ type: "spring", stiffness: 160, damping: 22 }}
          onAnimationComplete={onExitComplete}
          className="fixed top-0 right-0 h-full w-full sm:w-[980px] bg-white shadow-2xl z-50"
          role="dialog"
          aria-label="Department compliance"
        >
          <div className="h-14 flex items-center justify-between px-4 border-b">
            <h3 className="text-lg font-semibold text-slate-800 truncate flex items-center gap-2">
              {card.title}
              {reportingMonthLabel ? (
                <span className="text-sm font-normal text-slate-500 truncate">({reportingMonthLabel})</span>
              ) : null}
              {submitted[chartIndex] && !(isNursing && hydrationLoading) && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Submitted v{(versions[chartIndex] || 1).toString()}</span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              <button
                className="px-3 h-9 rounded-full border border-red-200 bg-red-50 text-red-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setClearOpen(true)}
                disabled={isExternallyReadOnly}
              >
                ล้างข้อมูลทั้งหมด
              </button>
              <button aria-label="Close" className="p-2 rounded-lg hover:bg-slate-100" onClick={onClose}>
                <X size={18} />
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="p-4 pb-28 sm:pb-16 space-y-6 overflow-y-auto h-[calc(100vh-56px)]"
          >
            <CrossAuditSliceFirestoreSync
              enabled={Boolean(card && isNursing)}
              isCrossAuditChart={isCrossAuditChart}
              auditedDepartmentId={departmentId}
              reportingMonth={reportingMonth}
              performedByDeptIdForFirestore={crossAuditFirestorePerformerId}
              crossAuditSlot={crossAuditSlotResolved}
              chartTitles={chartTitleList}
              chartCount={chartCount}
              chartIndex={chartIndex}
              hasLocalEdits={hasLocalEdits}
              hydrationLoading={Boolean(hydrationLoading)}
              hydrationToken={hydrationToken}
              onRemoteSlice={applyCrossAuditRemoteSlice}
            />
            {isNursing && hydrationLoading ? (
              <span className="sr-only" role="status">
                กำลังโหลดข้อมูลที่บันทึกไว้จากเซิร์ฟเวอร์ รวมถึงตรวจไขว้
              </span>
            ) : null}
            {crossAuditPartialLoadHint ? (
              <div
                className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                role="status"
              >
                ข้อมูลตรวจไขว้บางส่วนอาจไม่ตรงกับแถวในฟอร์ม (ชื่อแผนภูมิหรือช่องตรวจไขว้ไม่ตรงกัน)
                ลองรีเฟรชหน้าหรือแจ้งทีมไอทีหากข้อมูลยังไม่ครบ
              </div>
            ) : null}
            {card?.title === LAUNCH_CATEGORY_TITLE && NURSE_NOTES_XLSX_IMPORT_UI_ENABLED && (
              <NurseNotesXlsxImportPanel
                departmentId={
                  effectiveDepartmentIdForValidation
                    ? getFirestorePathDepartmentId(effectiveDepartmentIdForValidation)
                    : null
                }
                reportingMonth={reportingMonthKey}
                submittedByName={actorSubmissionMetadata.submittedByName ?? null}
                readOnly={isExternallyReadOnly}
                onImportSuccess={() => onReportingTableRefresh?.()}
              />
            )}
            {hasDetailedForm ? (
              <>
                <div className="flex flex-wrap gap-2">
                  {makeArray(chartCount, (i) => {
                    const canDelete = allowChartDeletion && i >= minChartsRequired;
                    return (
                      <div key={i} className="relative">
                        <button
                          className={`px-3 h-9 rounded-full text-sm border relative ${chartIndex === i ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"} ${
                            incomplete[i]
                              ? chartIndex === i
                                ? "bg-red-600 text-white border-red-600"
                                : "text-red-700 border-red-500 ring-1 ring-red-300"
                              : ""
                          }`}
                          onClick={() => {
                            setChartIndex(i);
                            setIsEditing(!submitted[i]);
                            setRowErrors(makeArray(questionCount || 0, () => false));
                            setShiftErrors((prev) => {
                              const n = [...prev];
                              n[i] = false;
                              return n;
                            });
                            setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
                            setTimeout(scrollTop, 0);
                          }}
                        >
                          {getChartTabLabel(i)}
                          {submitted[i] && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">v{(versions[i] || 1).toString()}</span>
                          )}
                          {!submitted[i] && hasDraft(i) && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title="ยังไม่ส่ง">
                              Draft
                            </span>
                          )}
                          {incomplete[i] && !submitted[i] && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">Incomplete</span>
                          )}
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            aria-label={`ลบ ${getChartTabLabel(i)}`}
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full border border-slate-300 bg-white text-[9px] font-semibold text-slate-500 shadow-sm hover:bg-red-50 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              setDeleteTarget(i);
                            }}
                            disabled={isExternallyReadOnly}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {(allowsExtraComplianceCharts(card?.title)) && (
                    <button
                      className="px-3 h-9 rounded-full border border-dashed border-slate-300 text-slate-700 text-sm hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={addNewChart}
                      disabled={isExternallyReadOnly}
                    >
                      {extraComplianceChartAddButtonLabel(card?.title, periodLabel)}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600 mt-1">
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded bg-emerald-500" aria-hidden></span>ส่งแล้ว
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded bg-amber-400" aria-hidden></span>ร่าง (Draft)
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-3 h-3 rounded bg-slate-300" aria-hidden></span>ยังไม่กรอก
                  </span>
                </div>

                <section className="space-y-2">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-slate-600">
                      {isSupervision
                        ? `กรอกข้อมูลนิเทศสำหรับ ${activeChartLabel}`
                        : `กรอกผลสำหรับ ${activeChartLabel}: 1 = ทำ, 0 = ไม่ทำ, NA = อื่นๆ`}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                      <label className="flex items-center gap-2">
                        <span>วันที่</span>
                        <input
                          id={`compliance-date-${chartIndex}`}
                          name="date"
                          type="date"
                          className={`h-10 rounded-full border px-3 text-sm ${
                            isSupervision && supervisionErrors.date ? "border-red-500 ring-1 ring-red-400" : ""
                          }`}
                          value={dates[chartIndex] || ""}
                          onChange={(e) => setDate(e.target.value)}
                          disabled={readonly}
                        />
                      </label>
                      {requiresHN && (
                        <label className="flex items-center gap-2">
                          <span>HN</span>
                          <input
                            id={`compliance-hn-${chartIndex}`}
                            name="hn"
                            className={`h-10 rounded-full border px-3 text-sm ${fieldErrors.hn ? "border-red-500 ring-1 ring-red-400" : ""}`}
                            value={hn[chartIndex] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              markHasLocalEdits();
                              setHn((arr) => {
                                const n = [...arr];
                                n[chartIndex] = value;
                                return n;
                              });
                              if (value) setFieldErrors((prev) => ({ ...prev, hn: false }));
                            }}
                            disabled={readonly}
                          />
                        </label>
                      )}
                      {requiresShiftSelection && (
                        <label className="flex items-center gap-2">
                          <span className="hidden sm:inline">เวร</span>
                          <select
                            className={`h-10 rounded-full border px-3 text-sm ${
                              shiftErrors[chartIndex] ? "border-red-500 ring-1 ring-red-400" : "border-slate-200"
                            }`}
                            value={shifts[chartIndex] || ""}
                            onChange={(e) => setShift(e.target.value)}
                            disabled={readonly}
                          >
                            <option value="">เลือกเวร</option>
                            <option value="morning">เช้า</option>
                            <option value="evening">บ่าย</option>
                            <option value="night">ดึก</option>
                          </select>
                        </label>
                      )}
                      {isCrossAuditChart && (
                        <label className="flex flex-col gap-1">
                          <span>ตรวจไขว้โดย</span>
                          <select
                            className="h-10 rounded-full border px-3 text-sm"
                            value={crossAuditByDeptIds[chartIndex] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              markHasLocalEdits();
                              setCrossAuditByDeptIds((arr) => {
                                const n = [...arr];
                                n[chartIndex] = value;
                                return n;
                              });
                            }}
                            disabled={readonly}
                          >
                            <option value="">-- เลือกตึก --</option>
                            {departmentsLoading && crossAuditDepartmentOptions.length === 0 && (
                              <option value="__loading__" disabled>
                                กำลังโหลดรายชื่อตึก...
                              </option>
                            )}
                            {!departmentsLoading && crossAuditDepartmentOptions.length === 0 && (
                              <option value="__empty__" disabled>
                                {currentLofId
                                  ? "ไม่พบตึกสำหรับตรวจไขว้"
                                  : "ไม่พบตึกสำหรับตรวจไขว้ (ยังไม่ระบุสายผู้ตรวจการพยาบาลของตึกคุณ) — กรุณารีเฟรช หรือแจ้งผู้ดูแลให้ตั้งค่าในระบบ"}
                              </option>
                            )}
                            {crossAuditDepartmentOptions.map((option) => (
                              <option key={option.id} value={option.id} disabled={option.id === auditorDepartmentId}>
                                {option.name}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>
                  </div>
                  {!isSupervision && (
                    <>
                      {card?.title === MED_ERROR_TITLE && (
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-1">
                          <p>ใส่ 1 กรณีที่ปฏิบัติถูกต้อง</p>
                          <p>ใส่ 0 กรณีที่ไม่ปฏิบัติหรือปฏิบัติไม่ถูกต้อง และบันทึกเหตุผลในช่องหมายเหตุ</p>
                          <p>ใส่ NA กรณีที่ไม่ต้องปฏิบัติ</p>
                        </div>
                      )}
                      <BulkChoiceFillButtons onFill={fillCurrentChartAnswers} disabled={readonly} />
                      <div className="space-y-3">
                        {checklistSections.map((section, sectionIdx) => (
                          <div key={sectionIdx} className="space-y-2">
                            {(section.title || section.description) && (
                              <div className="rounded-lg border bg-slate-50 px-3 py-2">
                                {section.title && <div className="text-sm font-semibold text-slate-800">{section.title}</div>}
                                {section.description && <div className="text-xs text-slate-600">{section.description}</div>}
                              </div>
                            )}
                            {section.rows.map(({ label, index }) => (
                              <div
                                key={index}
                                className={`flex flex-col gap-3 border rounded-xl p-3 sm:flex-row sm:items-start sm:justify-between ${
                                  readonly ? "opacity-75" : ""
                                }`}
                              >
                                <span className="text-sm sm:max-w-[50%]">{label}</span>
                                <div className="flex flex-wrap gap-2 sm:justify-end sm:items-center">
                                  <ChoiceGroup
                                    selected={sanitizeChoice((answers[chartIndex] || [])[index])}
                                    onSelect={(val) => {
                                      setAnswer(index, val);
                                      setRowErrors((prev) => {
                                        const n = [...prev];
                                        n[index] = false;
                                        return n;
                                      });
                                    }}
                                    disabled={readonly}
                                    error={rowErrors[index]}
                                  />
                                </div>
                              </div>
                            ))}
                            {section.note && (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                                {section.note}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="text-sm text-slate-700">
                        รวมคะแนน ({activeChartLabel}): {totals.score} / {totals.total} ({totals.pct}%)
                      </div>
                    </>
                  )}
                </section>

                {requiresClinicalInputs && (
                  <section className={`${readonly ? "opacity-75" : ""} space-y-3`}>
                    <h4 className="text-sm font-semibold text-slate-800">ร้อยละความสอดคล้องของบันทึกทางการพยาบาลกับแผนการรักษาของแพทย์ ({activeChartLabel})</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <label className="text-sm">
                        <span className="block text-slate-600 mb-1">โรค</span>
                        <input
                          id={`compliance-disease-${chartIndex}`}
                          name="disease"
                          className={`w-full border rounded-lg p-2 ${fieldErrors.disease ? "border-red-500 ring-1 ring-red-400" : ""}`}
                          value={disease[chartIndex] || ""}
                          onChange={(e) => {
                            markHasLocalEdits();
                            setDisease((arr) => {
                              const n = [...arr];
                              n[chartIndex] = e.target.value;
                              return n;
                            });
                            if (e.target.value) setFieldErrors((p) => ({ ...p, disease: false }));
                          }}
                          disabled={readonly}
                        />
                      </label>
                      <label className="text-sm">
                        <span className="block text-slate-600 mb-1">จำนวน Focus list ที่สอดคล้องกับการรักษาของแพทย์</span>
                        <input
                          id={`compliance-focus-${chartIndex}`}
                          name="focus"
                          className={`w-full border rounded-lg p-2 ${fieldErrors.focus ? "border-red-500 ring-1 ring-red-400" : ""}`}
                          inputMode="numeric"
                          value={focus[chartIndex] || ""}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, "");
                            markHasLocalEdits();
                            setFocus((arr) => {
                              const n = [...arr];
                              n[chartIndex] = v;
                              return n;
                            });
                            if (v) setFieldErrors((p) => ({ ...p, focus: false }));
                          }}
                          disabled={readonly}
                        />
                      </label>
                      <label className="text-sm">
                        <span className="block text-slate-600 mb-1">จำนวนการรักษาที่แพทย์บันทึก</span>
                        <input
                          id={`compliance-treatments-${chartIndex}`}
                          name="treatments"
                          className={`w-full border rounded-lg p-2 ${fieldErrors.treatments ? "border-red-500 ring-1 ring-red-400" : ""}`}
                          inputMode="numeric"
                          value={treatments[chartIndex] || ""}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, "");
                            markHasLocalEdits();
                            setTreatments((arr) => {
                              const n = [...arr];
                              n[chartIndex] = v;
                              return n;
                            });
                            if (v) setFieldErrors((p) => ({ ...p, treatments: false }));
                          }}
                          disabled={readonly}
                        />
                      </label>
                    </div>
                    <div className="text-sm text-slate-700">ร้อยละความสอดคล้อง: {consistencyPct}%</div>
                    <div className="text-sm text-slate-600">
                      ร้อยละความสอดคล้องภาพรวม (เฉลี่ยทุก Chart)
                      {overallNursingConsistencyPct != null
                        ? `: ${overallNursingConsistencyPct}%`
                        : ": —"}
                    </div>
                  </section>
                )}

                {allowFreeText && (
                  <section className={`${readonly ? "opacity-75" : ""} space-y-3`}>
                    <h4 className="text-sm font-semibold text-slate-800">
                      {card?.title === MED_ERROR_TITLE
                        ? `หมายเหตุ / เหตุผล (${activeChartLabel})`
                        : `ปัญหาที่พบภาพรวม (${activeChartLabel})`}
                    </h4>
                    <div className="space-y-2">
                      {flatList.map((label, idx) => (
                        <label key={label} className="block">
                          <span className="block text-xs text-slate-500 mb-1">{label}</span>
                          <textarea
                            className="w-full border rounded-lg p-2 text-sm"
                            rows={2}
                            value={(problems[chartIndex] || [])[idx] || ""}
                            onChange={(e) => setProblem(idx, e.target.value)}
                            disabled={readonly}
                          />
                        </label>
                      ))}
                    </div>
                    <label className="block text-sm">
                      <span className="block text-slate-600 mb-1">ข้อเสนอแนะอื่นๆ ({activeChartLabel})</span>
                      <textarea
                        className="w-full border rounded-lg p-2 text-sm"
                        rows={3}
                        value={suggestions[chartIndex] || ""}
                        onChange={(e) =>
                          setSuggestions((arr) => {
                            markHasLocalEdits();
                            const n = [...arr];
                            n[chartIndex] = e.target.value;
                            return n;
                          })
                        }
                        disabled={readonly}
                      />
                    </label>
                  </section>
                )}

                {!isSupervision && (
                  <section className="text-sm text-slate-700">
                    สรุปรายเดือน (รวมทุก {isNursing ? baseChartCount : chartCount} {summaryLabel}):{" "}
                    {monthlyTotals.score} / {monthlyTotals.total} ({monthlyTotals.pct}%)
                  </section>
                )}

                {isSupervision && (
                  <section className={`${readonly ? "opacity-75" : ""} space-y-6`}>
                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2">ผู้รับการนิเทศ</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {SUPERVISION_RECIPIENT_OPTIONS.map((option) => (
                          <label key={option.value} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              className={`h-4 w-4 rounded border ${
                                supervisionErrors.recipients ? "border-red-500 ring-1 ring-red-400" : "border-slate-300"
                              }`}
                              checked={currentSupervisionRecord.recipients.includes(option.value)}
                              onChange={() => toggleRecipient(option.value)}
                              disabled={readonly}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                      {currentSupervisionRecord.recipients.includes("custom") && (
                        <input
                          className={`mt-2 w-full rounded-lg border p-2 text-sm ${
                            supervisionErrors.recipientsDetail ? "border-red-500 ring-1 ring-red-400" : ""
                          }`}
                          placeholder="โปรดระบุผู้รับการนิเทศ"
                          value={currentSupervisionRecord.recipientsDetail}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSupervisionRecord((record) => ({ ...record, recipientsDetail: value }));
                            if (value.trim()) setSupervisionErrors((prev) => ({ ...prev, recipientsDetail: false }));
                          }}
                          disabled={readonly}
                        />
                      )}
                      {supervisionErrors.recipients && (
                        <p className="mt-1 text-xs text-red-600">กรุณาเลือกผู้รับการนิเทศอย่างน้อย 1 รายการ</p>
                      )}
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2">กิจกรรมการนิเทศ</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {SUPERVISION_ACTIVITY_OPTIONS.map((option) => (
                          <label key={option.value} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className={`mt-1 h-4 w-4 rounded border ${
                                supervisionErrors.activities ? "border-red-500 ring-1 ring-red-400" : "border-slate-300"
                              }`}
                              checked={currentSupervisionRecord.activities.includes(option.value)}
                              onChange={() => toggleActivity(option.value)}
                              disabled={readonly}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                      {supervisionErrors.activities && (
                        <p className="mt-1 text-xs text-red-600">กรุณาเลือกกิจกรรมอย่างน้อย 1 รายการ</p>
                      )}
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-slate-800 mb-2">ประเด็นปัญหา</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {SUPERVISION_ISSUE_OPTIONS.map((option) => (
                          <label key={option.value} className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              className={`mt-1 h-4 w-4 rounded border ${
                                supervisionErrors.issues ? "border-red-500 ring-1 ring-red-400" : "border-slate-300"
                              }`}
                              checked={currentSupervisionRecord.issues.includes(option.value)}
                              onChange={() => toggleIssue(option.value)}
                              disabled={readonly}
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                      {currentSupervisionRecord.issues.includes("other") && (
                        <input
                          className={`mt-2 w-full rounded-lg border p-2 text-sm ${
                            supervisionErrors.issueOther ? "border-red-500 ring-1 ring-red-400" : ""
                          }`}
                          placeholder="ระบุประเด็นปัญหาอื่นๆ"
                          value={currentSupervisionRecord.issueOther}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSupervisionRecord((record) => ({ ...record, issueOther: value }));
                            if (value.trim()) setSupervisionErrors((prev) => ({ ...prev, issueOther: false }));
                          }}
                          disabled={readonly}
                        />
                      )}
                      {supervisionErrors.issues && (
                        <p className="mt-1 text-xs text-red-600">กรุณาเลือกประเด็นปัญหาอย่างน้อย 1 รายการ</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                      <label className="text-sm">
                        <span className="mb-1 block text-slate-600">สรุปรายละเอียดของปัญหา</span>
                        <textarea
                          className={`w-full rounded-lg border p-2 text-sm ${
                            supervisionErrors.problemDetail ? "border-red-500 ring-1 ring-red-400" : ""
                          }`}
                          rows={3}
                          value={currentSupervisionRecord.problemDetail}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSupervisionRecord((record) => ({ ...record, problemDetail: value }));
                            if (value.trim()) setSupervisionErrors((prev) => ({ ...prev, problemDetail: false }));
                          }}
                          disabled={readonly}
                        />
                      </label>
                      <label className="text-sm">
                        <span className="mb-1 block text-slate-600">แนวทางแก้ไข</span>
                        <textarea
                          className={`w-full rounded-lg border p-2 text-sm ${
                            supervisionErrors.solution ? "border-red-500 ring-1 ring-red-400" : ""
                          }`}
                          rows={3}
                          value={currentSupervisionRecord.solution}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSupervisionRecord((record) => ({ ...record, solution: value }));
                            if (value.trim()) setSupervisionErrors((prev) => ({ ...prev, solution: false }));
                          }}
                          disabled={readonly}
                        />
                      </label>
                      <label className="text-sm">
                        <span className="mb-1 block text-slate-600">แผนการติดตามผล</span>
                        <textarea
                          className={`w-full rounded-lg border p-2 text-sm ${
                            supervisionErrors.followUp ? "border-red-500 ring-1 ring-red-400" : ""
                          }`}
                          rows={3}
                          value={currentSupervisionRecord.followUp}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateSupervisionRecord((record) => ({ ...record, followUp: value }));
                            if (value.trim()) setSupervisionErrors((prev) => ({ ...prev, followUp: false }));
                          }}
                          disabled={readonly}
                        />
                      </label>
                    </div>
                  </section>
                )}
              </>
            ) : checklistSections.length ? (
              <>
                <div className="text-sm text-slate-600">Monthly checklist preview</div>
                <div className="space-y-3">
                  {checklistSections.map((section, sectionIdx) => (
                    <div key={sectionIdx} className="space-y-1">
                      {section.title && <div className="text-sm font-semibold text-slate-800">{section.title}</div>}
                      {section.description && (
                        <div className="text-xs text-slate-600">{section.description}</div>
                      )}
                      {section.rows.map(({ label }, rowIdx) => (
                        <label key={label} className="flex items-start gap-2 text-sm">
                          <input
                            type="checkbox"
                            id={`compliance-checklist-${sectionIdx}-${rowIdx}`}
                            name={`checklist-${sectionIdx}-${rowIdx}`}
                            className="mt-1"
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                      {section.note && <div className="text-xs text-amber-700">{section.note}</div>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                ไม่พบแบบฟอร์มสำหรับหมวดหมู่ {card?.title ?? "นี้"} กรุณาติดต่อผู้ดูแลระบบเพื่อกำหนดแบบฟอร์มให้ครบถ้วน
              </div>
            )}

            {persistenceError && (
              <div
                className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                role="alert"
              >
                <Circle className="h-4 w-4" />
                <span>{persistenceError}</span>
              </div>
            )}

            {hasDetailedForm && draftSavedAt && (
              <div className="flex items-center gap-2 text-xs text-emerald-700" role="status">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
                <span>Draft saved</span>
              </div>
            )}

            {hasDetailedForm && (
              <div className="sticky bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 pt-3 pb-4 shadow-[0_-4px_12px_rgba(15,23,42,0.08)]">
                <div className="flex flex-wrap gap-2 items-center">
                  {!submitted[chartIndex] && (
                    <button
                      className="px-3 h-10 rounded-full bg-slate-900 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => void submitCurrentChart("stay")}
                      disabled={isExternallyReadOnly}
                    >
                      ส่ง (Submit) เฉพาะรายการนี้
                    </button>
                  )}
                  {submitted[chartIndex] && !isEditing && (
                    <button
                      className="px-3 h-10 rounded-full bg-slate-900 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={startEdit}
                      disabled={isExternallyReadOnly}
                    >
                      แก้ไข
                    </button>
                  )}
                  {submitted[chartIndex] && isEditing && (
                    <button
                      className="px-3 h-10 rounded-full bg-slate-900 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={saveResubmission}
                      disabled={isExternallyReadOnly}
                    >
                      บันทึกการส่งใหม่
                    </button>
                  )}
                  <div className="ml-auto flex gap-2">
                    <button
                      className="px-3 h-10 rounded-full bg-slate-100 text-slate-800 text-sm"
                      onClick={() => {
                        setChartIndex((i) => {
                          const n = Math.max(0, i - 1);
                          setIsEditing(!submitted[n]);
                          setRowErrors(makeArray(questionCount || 0, () => false));
                          setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
                          setTimeout(scrollTop, 0);
                          return n;
                        });
                      }}
                    >
                      ก่อนหน้า
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-slate-100 text-slate-800 text-sm"
                      onClick={() => {
                        if (!submitted[chartIndex]) {
                          if (isExternallyReadOnly) {
                            setChartIndex((i) => {
                              const n = Math.min(chartCount - 1, i + 1);
                              setIsEditing(!submitted[n]);
                              setRowErrors(makeArray(questionCount || 0, () => false));
                              setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
                              setTimeout(scrollTop, 0);
                              return n;
                            });
                            return;
                          }
                          setNavOpen(true);
                          return;
                        }
                        setChartIndex((i) => {
                          const n = Math.min(chartCount - 1, i + 1);
                          setIsEditing(!submitted[n]);
                          setRowErrors(makeArray(questionCount || 0, () => false));
                          setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
                          setTimeout(scrollTop, 0);
                          return n;
                        });
                      }}
                    >
                      ถัดไป
                    </button>
                    <button className="px-3 h-10 rounded-full bg-slate-100 text-slate-800 text-sm" onClick={onClose}>
                      ปิด
                    </button>
                  </div>
                </div>
              </div>
            )}

            {hasDetailedForm && history[chartIndex] && history[chartIndex].length > 0 && (
              <div className="pt-2 text-xs text-slate-500">
                History v{versions[chartIndex]} — {history[chartIndex].length} submission(s). Latest used for reports.
              </div>
            )}

            {hasDetailedForm && confirmOpen && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-lg w-[92%] p-5">
                  <h4 className="text-base font-semibold text-slate-800 mb-1">ท่านยังกรอกข้อมูลไม่ครบ</h4>
                  <p className="text-sm text-slate-600 mb-3">กรุณาตรวจสอบหัวข้อดังต่อไปนี้ หรือกดส่งต่อไปเพื่อบันทึกพร้อมคำเตือน</p>
                  <ul className="list-disc pl-5 max-h-40 overflow-auto text-sm text-red-700">
                    {missing.map((m, i) => (
                      <li key={i} className="mb-1">
                        {m}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 flex gap-2 justify-end">
                    <button className="px-3 h-10 rounded-full bg-white border text-slate-800 text-sm" onClick={() => setConfirmOpen(false)}>
                      กลับไปแก้ไข
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-red-600 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        setConfirmOpen(false);
                        void finalizeSubmit(true, "stay");
                      }}
                      disabled={isExternallyReadOnly}
                    >
                      ส่งต่อไป
                    </button>
                  </div>
                </div>
              </div>
            )}

            {hasDetailedForm && deleteTarget !== null && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-lg w-[92%] p-5">
                  <h4 className="text-base font-semibold text-slate-800 mb-2">
                    ลบ{deleteTarget !== null ? getChartTabLabel(deleteTarget) : periodLabel}
                  </h4>
                  <p className="text-sm text-slate-600">
                    ข้อมูลของ{deleteTarget !== null ? getChartTabLabel(deleteTarget) : periodLabel} นี้จะถูกลบถาวร ยืนยันว่าต้องการลบใช่หรือไม่
                  </p>
                  <div className="mt-4 flex gap-2 justify-end flex-wrap">
                    <button className="px-3 h-10 rounded-full bg-white border text-slate-800 text-sm" onClick={() => setDeleteTarget(null)}>
                      ยกเลิก
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-red-600 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        if (deleteTarget !== null) {
                          deleteChart(deleteTarget);
                        }
                        setDeleteTarget(null);
                      }}
                      disabled={isExternallyReadOnly}
                    >
                      ลบ{deleteTarget !== null ? getChartTabLabel(deleteTarget) : periodLabel}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {hasDetailedForm && clearOpen && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-lg w-[92%] p-5">
                  <h4 className="text-base font-semibold text-slate-800 mb-2">ล้างข้อมูลทั้งหมด</h4>
                  <p className="text-sm text-slate-600">
                    การดำเนินการนี้จะลบข้อมูลที่กรอกทั้งหมดสำหรับทุก{summaryLabel}ของหมวด {card.title} ในเดือนนี้ ทั้งแบบร่างและที่บันทึกไว้บนเครื่องของคุณ
                  </p>
                  <div className="mt-4 flex gap-2 justify-end flex-wrap">
                    <button className="px-3 h-10 rounded-full bg-white border text-slate-800 text-sm" onClick={() => setClearOpen(false)}>
                      ยกเลิก
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-red-600 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={async () => {
                        setClearOpen(false);
                        await clearAllInputs();
                      }}
                      disabled={isExternallyReadOnly}
                    >
                      ล้างข้อมูลทั้งหมด
                    </button>
                  </div>
                </div>
              </div>
            )}

            {navOpen && !submitted[chartIndex] && (
              <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                <div className="bg-white rounded-2xl shadow-xl max-w-lg w-[92%] p-5">
                  <h4 className="text-base font-semibold text-slate-800 mb-1">ยังไม่ได้ส่งแบบฟอร์ม</h4>
                  <p className="text-sm text-slate-600 mb-3">ถ้าท่านไปหน้าถัดไป ข้อมูลที่กรอกไว้จะถูกบันทึกเป็นร่าง (Draft) และยังไม่ถือว่าส่ง</p>
                  <div className="mt-4 flex flex-wrap gap-2 justify-end">
                    <button className="px-3 h-10 rounded-full bg-white border text-slate-800 text-sm" onClick={() => setNavOpen(false)}>
                      ยกเลิก
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-slate-100 text-slate-800 text-sm"
                      onClick={() => {
                        setNavOpen(false);
                        setChartIndex((i) => {
                          const n = Math.min(chartCount - 1, i + 1);
                          setIsEditing(!submitted[n]);
                          setRowErrors(makeArray(questionCount || 0, () => false));
                          setFieldErrors({ disease: false, focus: false, treatments: false, hn: false });
                          setTimeout(scrollTop, 0);
                          return n;
                        });
                      }}
                    >
                      ไปหน้าถัดไป (บันทึกร่าง)
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-slate-900 text-white text-sm"
                      onClick={() => {
                        setNavOpen(false);
                        void submitCurrentChart("next");
                      }}
                      disabled={isExternallyReadOnly}
                    >
                      ส่งและไปหน้าถัดไป
                    </button>
                    <button
                      className="px-3 h-10 rounded-full bg-slate-900 text-white text-sm"
                      onClick={() => {
                        setNavOpen(false);
                        void submitCurrentChart("stay");
                      }}
                      disabled={isExternallyReadOnly}
                    >
                      ส่งและอยู่หน้าเดิม
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}

export default SidePanel;
